import { flag, home, homeOutline, pushOutline } from 'ionicons/icons';
import { assign } from 'lodash';
import {
  PlanEntitlementKey,
  ScratchpadBucket,
} from '../../../../../api/graphql/generated';
import { IntercomArticle } from '../../../../../config/intercom';
import { i18nKeyType } from '../../../../../i18n';
import { getLocalizedText } from '../../../../../i18n/i18n.utils';
import { URModel } from '../../../../../robot/manufacturers/universal-robots/ur.types';
import { QNear, Waypoint } from '../../../../../util/machine-variable.utils';
import {
  AngleModificationMode,
  WorkPiecePosition,
} from '../../../../../util/part.util';
import { moveItem } from '../../../shared/component/icon/move-item';
import { reportProduction } from '../../../shared/component/icon/report-production';
import { waitForOperator } from '../../../shared/component/icon/wait-for-operator';
import {
  ApplicationDefaultParameterSetTagData,
  BuildParameterValidationContext,
  Flow,
  FlowItem,
  FlowItemMetadata,
  FlowItemParameterMetadata,
  FlowItemParameterRefAdjustmentMetadata,
  FlowItemSectionAttributes,
  FlowItemSectionParent,
  FlowItemSectionParentAttributes,
  FlowRule,
  ParameterValue,
  ParametersForm,
  PickerWidget,
  ProgramNameMapping,
  PropertyValueUOM,
  RuleValidationResponse,
  Tool,
  ToolConfig,
  WidgetType,
} from '../../../shared/model/flow.types';
import {
  getParameterValue,
  getParameterValueFromForm,
} from '../../../shared/util/flow-parameter.util';
import { flowRuleCheckContiguousByType } from '../../../shared/util/flow-rule.util';
import { weldItem } from '../component/icon/weld-item';
import {
  isSingleItemWeldFromForm,
  sectionBreaksSequence,
} from '../util/beacon-welding.util';

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace BeaconWeldingData {
  export const APP_NAME = 'Beacon Welding';
  export const ANALYTICS_PREFIX = 'Cobot Welder';
  export const APP_SLUG = 'beacon_welding';
  export interface License {
    welder: string;
  }

  export const ProgramName: ProgramNameMapping = {
    move_to_point: 'welding:move_here',
    fine_tune: 'common:fine_tune',
  };

  export enum Welder {
    INVISION = 'invision',
    OTC_WELBEE = 'otc_welbee',
    ESAB_ARISTO = 'esab_aristo',
    MILLER_DELTAWELD = 'miller_deltaweld',
  }

  export enum WeldTypeEnum {
    LINEAR = 1,
    RADIAL = 2,
    TACK = 3,
    LINEAR_STITCH = 4,
    ROTARY = 5,
  }

  export enum WeavePatternEnum {
    TRIANGLE = 1,
    WHIP_STITCH = 2,
  }

  export enum WeldProcess {
    CV = 'cv',
    PULSE = 'pulse',
    MILLER_RMD = 'miller_rmd',
    MILLER_HD = 'miller_hd',

    // we can't make this value vendor specific since we have customers
    // already using it, however going forward we would prefer to make each
    // key and value vendor specific
    OTC_WAVE_PULSE = 'wave_pulse',
  }

  export enum BeaconWeldingTenantPreferences {
    LIBRARY_FORM_OPTIONS = 'beacon_welding.library.form_options_v2',
  }

  export enum BeaconWeldingUserPreferences {
    LIBRARY_HIDE_NOTE_POPUP = 'beacon_welding.library.hide_notes_popup',
  }

  export enum BeaconWeldingMachinePreference {
    PART_CONFIG_DEFAULTS = 'beacon_welding.part_config.defaults',
  }

  export enum ToolKey {
    TREGASKISS_22QC = 'tregaskiss_22qc',
    TREGASKISS_22QCL = 'tregaskiss_22qcl',
    TREGASKISS_22QCL1 = 'tregaskiss_22qcl1',
    TREGASKISS_45QC = 'tregaskiss_45qc',
    TREGASKISS_45QCL = 'tregaskiss_45qcl',
    TREGASKISS_45QCL1 = 'tregaskiss_45qcl1',
    ESAB_72_22_SHORT = 'esab_72_22short',
    ESAB_72_22_SHORT_PUSH_PULL = 'esab_72_22short_push_pull',
    ESAB_82_36_SHORT_PUSH_PULL = 'esab_82_36short_push_pull',
    ESAB_82_22_LONG = 'esab_82_22long',
    ESAB_82_45_SHORT_PUSH_PULL = 'esab_82_45short_push_pull',
    ESAB_82_45_LONG = 'esab_82_45long',
    ESAB_72_0 = 'esab_72_0',
    ESAB_72_0_PUSH_PULL = 'esab_72_0_push_pull',
  }

  export const Tools: Tool[] = [
    // There are simulators using UR5, so UR10 torches are setup with UR5 as well
    {
      id: ToolKey.TREGASKISS_22QC,
      label: 'Tregaskiss 22º Short (22QC)',
      config: {
        name: ToolKey.TREGASKISS_22QC,
        cog: [0, -0.093, 0.127],
        inertia: [0.068, 0.043, 0.027, 0.0000575, -0.000127, -0.006],
        mass: 3.81,
        tcp: {
          rx: -0.78,
          ry: 0,
          rz: 0,
          x: -0.0004,
          y: -0.000674,
          z: 0.424765,
        },
      },
      equipment: [Welder.INVISION, Welder.MILLER_DELTAWELD, Welder.OTC_WELBEE],
      models: [URModel.UR5, URModel.UR10],
    },
    {
      id: ToolKey.TREGASKISS_22QCL,
      label: 'Tregaskiss 22º Medium (22QCL)',
      config: {
        name: ToolKey.TREGASKISS_22QCL,
        cog: [-0.058, -0.063, 0.187],
        inertia: [0.068, 0.043, 0.027, 0.0000575, -0.000127, -0.006],
        mass: 3.52,
        tcp: {
          rx: -0.78,
          ry: 0,
          rz: 0,
          x: 0.0005,
          y: 0.0215,
          z: 0.477,
        },
      },
      equipment: [Welder.INVISION, Welder.MILLER_DELTAWELD, Welder.OTC_WELBEE],
      models: [URModel.UR5, URModel.UR10],
    },
    {
      id: ToolKey.TREGASKISS_22QCL1,
      label: 'Tregaskiss 22º Long (22QCL1)',
      config: {
        name: ToolKey.TREGASKISS_22QCL1,
        cog: [0.024, -0.096, 0.146],
        inertia: [0.068, 0.043, 0.027, 0.0000575, -0.000127, -0.006],
        mass: 3.62,
        tcp: {
          x: -0.00147,
          y: 0.04666,
          z: 0.53232,
          rx: -0.78,
          ry: 0,
          rz: 0,
        },
      },
      equipment: [Welder.INVISION, Welder.MILLER_DELTAWELD, Welder.OTC_WELBEE],
      models: [URModel.UR5, URModel.UR10],
    },
    {
      id: ToolKey.TREGASKISS_45QC,
      label: 'Tregaskiss 45º Short (45QC)',
      config: {
        name: ToolKey.TREGASKISS_45QC,
        cog: [0.024, -0.096, 0.146],
        inertia: [0.068, 0.043, 0.027, 0.0000575, -0.000127, -0.006],
        mass: 3.62,
        tcp: {
          x: 0,
          y: 0.03057,
          z: 0.35942,
          rx: -1.1781,
          ry: 0,
          rz: 0,
        },
      },
      equipment: [Welder.INVISION, Welder.MILLER_DELTAWELD, Welder.OTC_WELBEE],
      models: [URModel.UR5, URModel.UR10],
    },
    {
      id: ToolKey.TREGASKISS_45QCL,
      label: 'Tregaskiss 45º Medium (45QCL)',
      config: {
        name: ToolKey.TREGASKISS_45QCL,
        cog: [0.024, -0.096, 0.146],
        inertia: [0.068, 0.043, 0.027, 0.0000575, -0.000127, -0.006],
        mass: 3.62,
        tcp: {
          x: 0,
          y: 0.06599,
          z: 0.39447,
          rx: -1.1781,
          ry: 0,
          rz: 0,
        },
      },
      equipment: [Welder.INVISION, Welder.MILLER_DELTAWELD, Welder.OTC_WELBEE],
      models: [URModel.UR5, URModel.UR10],
    },
    {
      id: ToolKey.TREGASKISS_45QCL1,
      label: 'Tregaskiss 45º Long (45QCL1)',
      config: {
        name: ToolKey.TREGASKISS_45QCL1,
        cog: [0.011, -0.094, 0.14],
        inertia: [0.0178, 0.0178, 0.0178, 0.0000575, -0.000127, -0.006],
        mass: 4.35,
        tcp: {
          x: 0.00066,
          y: 0.0905,
          z: 0.4455,
          rx: -1.1781,
          ry: 0,
          rz: 0,
        },
      },
      equipment: [Welder.INVISION, Welder.MILLER_DELTAWELD, Welder.OTC_WELBEE],
      models: [URModel.UR5, URModel.UR10],
    },
    {
      id: ToolKey.ESAB_72_22_SHORT,
      label: 'ESAB-72 22º Short',
      config: {
        name: ToolKey.ESAB_72_22_SHORT,
        cog: [0.012, -0.072, 0.12],
        inertia: [0.010586, 0.010586, 0.010586, 0, 0, 0],
        mass: 3.18,
        tcp: {
          x: 0.00142,
          y: 0.00328,
          z: 0.36214,
          rx: -0.78,
          ry: 0,
          rz: 0,
        },
      },
      equipment: [Welder.ESAB_ARISTO],
      models: [URModel.UR5, URModel.UR10],
    },
    {
      id: ToolKey.ESAB_82_22_LONG,
      label: 'ESAB-82 22º Long',
      config: {
        name: ToolKey.ESAB_82_22_LONG,
        cog: [0.008, -0.072, 0.147],
        inertia: [0.012067, 0.012067, 0.012067, 0, 0, 0],
        mass: 3.44,
        tcp: {
          x: 0.00142,
          y: 0.07953,
          z: 0.53891,
          rx: -0.78,
          ry: 0,
          rz: 0,
        },
      },
      equipment: [Welder.ESAB_ARISTO],
      models: [URModel.UR5, URModel.UR10],
    },
    {
      id: ToolKey.ESAB_72_22_SHORT_PUSH_PULL,
      label: 'ESAB-72 22º Short - Push Pull',
      config: {
        name: ToolKey.ESAB_72_22_SHORT_PUSH_PULL,
        cog: [-0.007, -0.064, 0.159],
        inertia: [0.0158, 0.0158, 0.0158, 0, 0, 0],
        mass: 4.5,
        tcp: {
          x: 0,
          y: -0.063,
          z: 0.4566,
          rx: -0.39,
          ry: 0,
          rz: 0,
        },
      },
      equipment: [Welder.ESAB_ARISTO],
      models: [URModel.UR5, URModel.UR10],
    },
    {
      id: ToolKey.ESAB_82_36_SHORT_PUSH_PULL,
      label: 'ESAB-82 36º Short - Push Pull',
      config: {
        name: ToolKey.ESAB_82_36_SHORT_PUSH_PULL,
        cog: [-0.006, -0.063, 0.148],
        inertia: [0.018881, 0.018881, 0.018881, 0, 0, 0],
        mass: 4.5,
        tcp: {
          x: 0,
          y: -0.0284,
          z: 0.4453,
          rx: -0.6283,
          ry: 0,
          rz: 0,
        },
      },
      equipment: [Welder.ESAB_ARISTO],
      models: [URModel.UR5, URModel.UR10],
    },
    {
      id: ToolKey.ESAB_82_45_SHORT_PUSH_PULL,
      label: 'ESAB-82 45º Short - Push Pull',
      config: {
        name: ToolKey.ESAB_82_45_SHORT_PUSH_PULL,
        cog: [-0.012, -0.045, 0.114],
        inertia: [0.018881, 0.018881, 0.018881, 0, 0, 0],
        mass: 4.5,
        tcp: {
          x: 0,
          y: 0.004,
          z: 0.4203,
          rx: -0.7854,
          ry: 0,
          rz: 0,
        },
      },
      equipment: [Welder.ESAB_ARISTO],
      models: [URModel.UR5, URModel.UR10],
    },
    {
      id: ToolKey.ESAB_82_45_LONG,
      label: 'ESAB-82 45º Long',
      config: {
        name: ToolKey.ESAB_82_45_LONG,
        cog: [0.01, -0.073, 0.133],
        inertia: [0.013686, 0.013686, 0.013686, 0, 0, 0],
        mass: 3.71,
        tcp: {
          x: 0.00142,
          y: 0.13149,
          z: 0.47861,
          rx: -1.178,
          ry: 0,
          rz: 0,
        },
      },
      equipment: [Welder.ESAB_ARISTO],
      models: [URModel.UR5, URModel.UR10],
    },
    {
      id: ToolKey.ESAB_72_0,
      label: 'ESAB-72 0º',
      config: {
        name: ToolKey.ESAB_72_0,
        cog: [0.008, -0.085, 0.132],
        inertia: [0.01195, 0.01195, 0.01195, 0, 0, 0],
        mass: 3.42,
        tcp: {
          x: 0.0,
          y: -0.035,
          z: 0.386,
          rx: -0.401,
          ry: 0,
          rz: 0,
        },
      },
      equipment: [Welder.ESAB_ARISTO],
      models: [URModel.UR5, URModel.UR10],
    },
    {
      id: ToolKey.ESAB_72_0_PUSH_PULL,
      label: 'ESAB-72 0º - Push Pull',
      config: {
        name: ToolKey.ESAB_72_0_PUSH_PULL,
        cog: [-0.005, -0.068, 0.124],
        inertia: [0.01958, 0.01958, 0.01958, 0, 0, 0],
        mass: 4.5,
        tcp: {
          x: 0.0,
          y: -0.107,
          z: 0.465,
          rx: 0,
          ry: 0,
          rz: 0,
        },
      },
      equipment: [Welder.ESAB_ARISTO],
      models: [URModel.UR5, URModel.UR10],
    },
    // UR8_LONG torches
    {
      id: ToolKey.TREGASKISS_22QC,
      label: 'Tregaskiss 22º Short (22QC)',
      config: {
        name: ToolKey.TREGASKISS_22QC,
        cog: [-0.017, -0.07, 0.138],
        inertia: [0.016497, 0.016497, 0.016497, 0, 0, 0],
        mass: 4.2,
        tcp: {
          rx: -0.78,
          ry: 0,
          rz: 0,
          x: 0.0007,
          y: 0.00255,
          z: 0.42414,
        },
      },
      equipment: [Welder.INVISION, Welder.MILLER_DELTAWELD, Welder.OTC_WELBEE],
      models: [URModel.UR8_LONG],
    },
    // UR20 torches
    {
      id: ToolKey.TREGASKISS_22QC,
      label: 'Tregaskiss 22º Short (22QC)',
      config: {
        name: ToolKey.TREGASKISS_22QC,
        cog: [0.005, -0.113, 0.182],
        inertia: [0.068, 0.043, 0.027, 0.0000575, -0.000127, -0.006],
        mass: 4.36,
        tcp: {
          rx: -0.78,
          ry: 0,
          rz: 0,
          x: 0.0002,
          y: 0.00012,
          z: 0.4427,
        },
      },
      equipment: [Welder.INVISION, Welder.MILLER_DELTAWELD, Welder.OTC_WELBEE],
      models: [URModel.UR20],
    },
    {
      id: ToolKey.TREGASKISS_22QCL1,
      label: 'Tregaskiss 22º Long (22QCL1)',
      config: {
        name: ToolKey.TREGASKISS_22QCL1,
        cog: [-0.031, -0.145, 0.122],
        inertia: [0.020516, 0.020516, 0.020516, 0, 0, 0],
        mass: 4.7,
        tcp: {
          rx: -0.78,
          ry: 0,
          rz: 0,
          x: 0.0049,
          y: 0.048,
          z: 0.5461,
        },
      },
      equipment: [Welder.INVISION, Welder.MILLER_DELTAWELD, Welder.OTC_WELBEE],
      models: [URModel.UR20],
    },
    {
      id: ToolKey.ESAB_72_22_SHORT,
      label: 'ESAB-72 22º Short',
      config: {
        name: ToolKey.ESAB_72_22_SHORT,
        cog: [-0.022, -0.059, 0.12],
        inertia: [0.010586, 0.010586, 0.010586, 0, 0, 0],
        mass: 3.35,
        tcp: {
          x: 0.00142,
          y: 0.00328,
          z: 0.36214,
          rx: -0.78,
          ry: 0,
          rz: 0,
        },
      },
      equipment: [Welder.ESAB_ARISTO],
      models: [URModel.UR20],
    },
    {
      id: ToolKey.ESAB_72_22_SHORT_PUSH_PULL,
      label: 'ESAB-72 22º Short - Push Pull',
      config: {
        name: ToolKey.ESAB_72_22_SHORT_PUSH_PULL,
        cog: [-0.008, -0.07, 0.145],
        inertia: [0.0176, 0.0176, 0.0176, 0, 0, 0],
        mass: 4.5,
        tcp: {
          x: 0,
          y: -0.066,
          z: 0.472,
          rx: -0.39,
          ry: 0,
          rz: 0,
        },
      },
      equipment: [Welder.ESAB_ARISTO],
      models: [URModel.UR20],
    },
    {
      id: ToolKey.ESAB_72_0_PUSH_PULL,
      label: 'ESAB-72 0º - Push Pull',
      config: {
        name: ToolKey.ESAB_72_0_PUSH_PULL,
        cog: [-0.008, -0.07, 0.145],
        inertia: [0.0176, 0.0176, 0.0176, 0, 0, 0],
        mass: 4.5,
        tcp: {
          x: 0.0,
          y: -0.11,
          z: 0.48,
          rx: 0,
          ry: 0,
          rz: 0,
        },
      },
      equipment: [Welder.ESAB_ARISTO],
      models: [URModel.UR20],
    },
    {
      id: ToolKey.ESAB_82_45_LONG,
      label: 'ESAB-82 45º Long',
      config: {
        name: ToolKey.ESAB_82_45_LONG,
        cog: [0, -0.15, 0.172],
        inertia: [0.013686, 0.013686, 0.013686, 0, 0, 0],
        mass: 4.2,
        tcp: {
          x: -0.00145,
          y: 0.1315,
          z: 0.496,
          rx: -1.178,
          ry: 0,
          rz: 0,
        },
      },
      equipment: [Welder.ESAB_ARISTO],
      models: [URModel.UR20],
    },
  ];

  // Parameter Set Data
  export enum Gas {
    GAS_100H = '100H',
    GAS_90H_7_5A_2_5CO2 = '90H_7_5A_2_5CO2',
    GAS_75H_25A = '75H_25A',
    GAS_50H_50A = '50H_50A',
    GAS_25H_75A = '25H_75A',
    GAS_100CO2 = '100CO2',
    GAS_100A = '100A',
    GAS_98A_2CO2 = '98A_2CO2',
    GAS_95A_5CO2 = '95A_5CO2',
    GAS_90A_10CO2 = '90A_10CO2',
    GAS_85A_15CO2 = '85A_15CO2',
    GAS_80A_20CO2 = '80A_20CO2',
    GAS_81A_18H_1CO2 = '81A_18H_1CO2',
    GAS_66A_33H_1CO2 = '66A_33H_1CO2',
    GAS_75A_25CO2 = '75A_25CO2',
    GAS_95A_5O = '95A_5O',
    GAS_98A_2O = '98A_2O',
  }

  export enum ArcalGas {
    CHROME = 'arcal_chrome',
    FLUX = 'arcal_flux',
    FORCE = 'arcal_force',
    PRIME = 'arcal_prime',
    SPEED = 'arcal_speed',
    TECH = 'arcal_tech',
  }

  // Unit = millimeters
  // This data comes from the Miller Welding Calculator
  // https://www.millerwelds.com/resources/weld-setting-calculators/mig-solid-core-welding-calculator
  export enum MaterialThickness {
    '22GA' = 0.8,
    '20GA' = 0.9,
    '18GA' = 1.2,
    '16GA' = 1.6,
    '14GA' = 2.0,
    '1_8' = 3.2,
    '3_16' = 4.8,
    '1_4' = 6.4,
    '5_16' = 7.9,
    '3_8' = 9.5,
    '1_2' = 12.7,
  }

  // Unit = inches
  export enum WireSize {
    '030' = 0.03,
    '035' = 0.035,
    '040' = 0.04,
    '045' = 0.045,
    '047' = 0.047,
    '052' = 0.052,
    '0625' = 0.0625,
    '078' = 0.078,
    '093' = 0.093,
  }

  export const CO2_20 = [Gas.GAS_75A_25CO2, ArcalGas.FORCE, ArcalGas.FLUX];
  export const O2_2 = [Gas.GAS_95A_5O, Gas.GAS_98A_2O, ArcalGas.CHROME];
  export const AR_100 = [Gas.GAS_100A, ArcalGas.PRIME];

  export enum Material {
    STEEL = 'steel',
    STAINLESS_STEEL = 'stainless_steel',
    STAINLESS_STEEL_FERRITE = 'stainless_steel_400',
    ALUMINUM_AL4X = 'aluminum_al4x',
    ALUMINUM_AL49X = 'aluminum_al49x',
    ALUMINUM_AL5X = 'aluminum_al5x',
    CU_AL = 'CuAl',
    CU_SI = 'CuSi',
    INCONEL = 'inconel',
  }

  export enum WireType {
    SOLID = 'solid_core',
    METAL = 'metal_core',
    FLUX = 'flux_core',
  }

  export interface WeldLbraryFormOptionsPreferenceValue {
    default: ApplicationDefaultParameterSetTagData;
    [welder: string]: ApplicationDefaultParameterSetTagData;
  }

  // Flow Section

  export interface FlowConfig {
    arcOn: boolean;
    home: {
      waypoint: Waypoint;
      qnear: QNear;
    };
    ioMapping: {
      input: {
        capture: string;
      };
      output: {
        light: string;
      };
    };
    torch: ToolConfig;
    welder: {
      model: string;
      ioMap: {
        mode: string;
        trigger: string;
      };
      customFields?: Record<string, unknown>;
    };
    wireDiameter: number;
    materialType: string;
    materialThickness?: number;
    wireType?: string;
    gas?: string;
    betweenSpeed: number;
    gasFlowRate: number;
    arcDetectionFailureTime?: number;
    startAtId?: string;
    stepModeEnabled?: boolean;
    weave?: {
      disableCornerRatioCheck?: boolean;
    };
  }

  export enum FlowItemRef {
    WELD = 'weld',
    MOVE = 'move',
    MOVE_HOME = 'move to home',
    WAIT_FOR = 'wait for operator',
    PRODUCTION_REPORTING = 'report production',
    TRIGGER_OUTPUT = 'trigger output',
    START = 'start',
    END = 'end',
    SECTION = 'section',
  }

  export enum FlowItemParameterRef {
    APPROACH = 'approach',
    ARC_CHARACTERISTIC = 'arcCharacteristic',
    ARC_LENGTH = 'arcLength',
    ARC_ON = 'arcOn',
    ARC_START_TIME = 'arcStartTime',
    CRATER_FILL_TIME = 'craterFillTime',
    DEPART = 'depart',
    EN_RATIO = 'enRatio',
    MOVE_MOTION_TYPE = 'moveMotionType',
    MOVE_SPEED_ADJUSTMENT_RATIO = 'moveSpeedAdjustmentRatio',
    MOVE_TRAVEL_SPEED = 'moveTravelSpeed',
    MOVE_BLEND_RADIUS = 'moveBlendRadius',
    POINT = 'point',
    PROCESS = 'process',
    PENETRATION_CONTROL = 'penetrationControl',
    PRESET_ARC_CONTROL = 'presetArcControl',
    PRODUCTION_QUANTITY = 'productionQuanity',
    QNEAR = 'qnear',
    SEQUENCE_PREFLOW_TIME = 'sequencePreflowTime',
    SEQUENCE_POSTFLOW_TIME = 'sequencePostflowTime',
    SEQUENCE_PROFILE_PULSE_MODE = 'sequenceProfilePulseMode',
    SECTION_COLOR = 'sectionColor',
    STICKOUT = 'stickOut',
    STITCH_NUM = 'stitchNumber',
    STITCH_LENGTH = 'stitchLength',
    SYNERGIC_MODE = 'synergicMode',
    SYNERGIC_MODE_FINE_ADJUSTMENT = 'voltageSynergyFineAdjustment',
    TACK_HOLD = 'tackHold',
    TRAVEL_SPEED = 'travelSpeed',
    TRIGGER_OUTPUT_PORT = 'triggerOutputPort',
    TRIGGER_OUTPUT_STATE = 'triggerOutputState',
    USE_WIRE_FEED_SPEED = 'useWireFeedSpeed',
    VOLTAGE = 'voltage',
    WAIT_FOR_OPERATOR_MESSAGE = 'operatorMessage',
    WAIT_FOR_INPUT = 'waitForInput',
    WAIT_FOR_TIME = 'waitForTime',
    WAIT_FOR_INPUT_STATE = 'waitForInputState',
    WAIT_FOR_TYPE_OF_INPUT = 'typeOfInput',
    WAVE_FREQUENCY = 'waveFrequency',
    WAYPOINT = 'waypoint',
    WEAVE_DWELL_TIME_1 = 'dwellTime1',
    WEAVE_DWELL_TIME_2 = 'dwellTime2',
    WEAVE_ENABLED = 'weaveEnabled',
    WEAVE_FREQUENCY = 'weaveFrequency',
    WEAVE_PATH_OFFSET_1 = 'weavePathOffset1',
    WEAVE_PATH_OFFSET_2 = 'weavePathOffset2',
    WEAVE_PATTERN = 'weavePattern',
    WELDING_CURRENT = 'weldingCurrent',
    WELD_TYPE = 'weldType',
    WIRE_FEED_SPEED = 'wireFeedSpeed',
    SEAM_TRACKING_ENABLED = 'seamTrackingEnabled',
    SEAM_TRACKING_LATERAL_ENABLED = 'seamTrackingLateralEnabled',
    SEAM_TRACKING_LATERAL_TRACK_RIGHT = 'lateralTrackRight',
    SEAM_TRACKING_LATERAL_TRACK_LEFT = 'lateralTrackLeft',
    SEAM_TRACKING_LATERAL_SENSITIVITY = 'lateralSensitivity',
    SEAM_TRACKING_VERTICAL_ENABLED = 'seamTrackingVerticalEnabled',
    SEAM_TRACKING_HEIGHT_SENSITIVITY = 'heightSensitivity',
  }

  export enum FlowItemParameterTag {
    APPROACH = 'approach',
    DEPART = 'depart',
    ROTARY = 'rotary',
    STITCH = 'stitch',
    SYNERGIC_MODE = 'synergicMode',
    TACK = 'tack',
    PROCESS_CV = 'cv',
    PROCESS_PULSE = 'pulse',
    PROCESS_MILLER_RMD = 'miller_rmd',
    PROCESS_MILLER_HD = 'miller_hd',
    PROCESS_OTC_WAVE_PULSE = 'wave_pulse',
    WEAVE = 'weave',
    WAIT_FOR_INPUT = 'waitForInput',
    WAIT_FOR_TIME = 'waitForTime',
    WAIT_FOR_OPERATOR = 'waitForOperator',
    USE_WIRE_FEED_SPEED = 'useWireFeedSpeed',
  }

  export type AppFlowItemParameterRefAdjustmentMetadata =
    FlowItemParameterRefAdjustmentMetadata<BeaconWeldingData.FlowItemParameterRef>;

  // Parameter Metadata

  export const ProcessMetadata: FlowItemParameterMetadata = {
    ref: FlowItemParameterRef.PROCESS,
    apiName: 'process',
    name: 'welding:welding_process',
    tags: [
      FlowItemParameterTag.PROCESS_CV,
      FlowItemParameterTag.PROCESS_PULSE,
      FlowItemParameterRef.APPROACH,
    ],
    displayOrder: 2,
    ui: {
      widget: WidgetType.PICKER,
      widgetOptions: [
        {
          label: 'welding:pulse',
          value: 'pulse',
        },
        {
          label: 'welding:cv',
          value: 'cv',
        },
      ],
    },
    value: {
      type: 'string',
      default: 'pulse',
    },
  };

  export const ArcOnMetadata: FlowItemParameterMetadata = {
    ref: FlowItemParameterRef.ARC_ON,
    apiName: 'arcOn',
    name: 'welding:arc_enabled',
    value: {
      type: 'boolean',
      default: true,
    },
  };

  export const ApproachMetadata: FlowItemParameterMetadata = {
    ref: FlowItemParameterRef.APPROACH,
    apiName: 'approach',
    name: 'welding:approach_weld',
    value: {
      type: 'boolean',
      default: false,
    },
  };

  export const DepartMetadata: FlowItemParameterMetadata = {
    ref: FlowItemParameterRef.DEPART,
    apiName: 'depart',
    name: 'welding:depart_weld',
    value: {
      type: 'boolean',
      default: false,
    },
  };

  export const ArcLengthMetadata: FlowItemParameterMetadata = {
    ref: FlowItemParameterRef.ARC_LENGTH,
    apiName: 'arcLength',
    name: 'welding:arc_length',
    description: 'welding:arc_length_desc',
    tags: [
      FlowItemParameterTag.PROCESS_PULSE,
      FlowItemParameterTag.PROCESS_OTC_WAVE_PULSE,
      FlowItemParameterTag.PROCESS_MILLER_RMD,
    ],
    displayOrder: 30,
    ui: {
      widget: WidgetType.NUMBER,
      widgetOptions: {
        step: 1,
      },
    },
    value: {
      type: 'number',
      default: 50,
      maximum: 100,
      minimum: 0,
    },
  };

  export const WireFeedSpeedMetadata: FlowItemParameterMetadata = {
    ref: FlowItemParameterRef.WIRE_FEED_SPEED,
    apiName: 'wireFeedSpeed',
    name: 'welding:wire_feed_speed',
    description: 'welding:wire_feed_speed_desc',
    tags: [
      FlowItemParameterTag.PROCESS_CV,
      FlowItemParameterTag.PROCESS_PULSE,
      FlowItemParameterTag.PROCESS_MILLER_RMD,
      FlowItemParameterTag.PROCESS_MILLER_HD,
    ],
    displayOrder: 20,
    value: {
      type: 'number',
      required: true,
      minimum: 70,
      maximum: 750,
      uom: {
        target: 'in/min',
        system: {
          imperial: 'in/min',
          metric: 'm/min',
        },
        precision: 3,
      },
    },
    ui: {
      widget: WidgetType.NUMBER,
      widgetOptions: {
        step: 0.01,
      },
    },
  };

  export const TravelSpeedMetadata: FlowItemParameterMetadata = {
    ref: FlowItemParameterRef.TRAVEL_SPEED,
    apiName: 'travelSpeed',
    name: 'welding:travel_speed',
    description: 'welding:travel_speed_desc',
    tags: [
      FlowItemParameterTag.PROCESS_CV,
      FlowItemParameterTag.PROCESS_PULSE,
      FlowItemParameterTag.PROCESS_OTC_WAVE_PULSE,
      FlowItemParameterTag.PROCESS_MILLER_RMD,
      FlowItemParameterTag.PROCESS_MILLER_HD,
    ],
    displayOrder: 10,
    ui: {
      widget: WidgetType.NUMBER,
      widgetOptions: {
        step: 0.1,
      },
    },
    value: {
      type: 'number',
      required: true,
      minimum: 0.0423, // .1 inches per minute
      maximum: 38.1,
      uom: {
        target: 'mm/s',
        system: {
          imperial: 'in/min',
          metric: 'cm/min',
        },
        precision: 3,
      },
    },
  };

  export const VoltageMetadata: FlowItemParameterMetadata = {
    ref: FlowItemParameterRef.VOLTAGE,
    apiName: 'voltage',
    name: 'welding:voltage',
    description: 'welding:voltage_desc',
    tags: [
      FlowItemParameterTag.PROCESS_CV,
      FlowItemParameterTag.PROCESS_MILLER_HD,
    ],
    displayOrder: 30,
    ui: {
      widget: WidgetType.NUMBER,
      widgetOptions: {
        step: 0.1,
      },
    },
    value: {
      type: 'number',
      required: true,
      minimum: 10,
      maximum: 38,
    },
  };

  export const StickoutMetadata: FlowItemParameterMetadata = {
    ref: FlowItemParameterRef.STICKOUT,
    apiName: 'stickout',
    name: 'welding:stickout_offset',
    description: 'welding:stickout_offset_desc',
    tags: [
      FlowItemParameterTag.PROCESS_PULSE,
      FlowItemParameterTag.PROCESS_CV,
      FlowItemParameterTag.PROCESS_OTC_WAVE_PULSE,
      FlowItemParameterTag.PROCESS_MILLER_RMD,
      FlowItemParameterTag.PROCESS_MILLER_HD,
      FlowItemParameterTag.DEPART,
    ],
    displayOrder: 100,
    ui: {
      widget: WidgetType.SLIDER,
      widgetOptions: {
        step: 1,
      },
    },
    value: {
      type: 'number',
      default: 0,
      minimum: -10,
      maximum: 10,
      uom: {
        target: 'mm',
      },
    },
  };

  export const ArcStartTimeMetadata: FlowItemParameterMetadata = {
    ref: FlowItemParameterRef.ARC_START_TIME,
    apiName: 'arcStartTime',
    name: 'welding:arc_start_time',
    description: 'welding:arc_start_time_desc',
    tags: [FlowItemParameterTag.APPROACH],
    displayOrder: 500,
    ui: {
      widget: WidgetType.SLIDER,
      widgetOptions: {
        step: 0.1,
      },
    },
    value: {
      type: 'number',
      default: 0.1,
      minimum: 0,
      maximum: 1,
      uom: {
        target: 's',
      },
    },
  };

  export const CraterFillTimeMetadata: FlowItemParameterMetadata = {
    ref: FlowItemParameterRef.CRATER_FILL_TIME,
    apiName: 'craterFillTime',
    name: 'welding:crater_fill_time',
    description: 'welding:crater_fill_time_desc',
    tags: [FlowItemParameterTag.DEPART, FlowItemParameterTag.TACK],
    displayOrder: 600,
    ui: {
      widget: WidgetType.SLIDER,
      widgetOptions: {
        step: 0.1,
      },
    },
    value: {
      type: 'number',
      default: 0.5,
      minimum: 0,
      maximum: 2,
      uom: {
        target: 's',
      },
    },
  };

  export const StitchLengthMetadata: FlowItemParameterMetadata = {
    ref: FlowItemParameterRef.STITCH_LENGTH,
    apiName: 'stitchLength',
    name: 'welding:stitch_length',
    description: 'welding:stitch_length_desc',
    tags: [FlowItemParameterTag.STITCH],
    displayOrder: 5,
    ui: {
      widget: WidgetType.NUMBER,
      widgetOptions: {
        step: 0.001,
      },
    },
    value: {
      type: 'number',
      default: 25.4,
      minimum: 5,
      required: true,
      uom: {
        target: 'mm',
        system: {
          imperial: 'in',
          metric: 'mm',
        },
        precision: 3,
      },
    },
  };

  export const NumOfStitchesMetadata: FlowItemParameterMetadata = {
    ref: FlowItemParameterRef.STITCH_NUM,
    apiName: 'numOfStitches',
    name: 'welding:num_of_stitches',
    description: 'welding:num_of_stitches_desc',
    displayOrder: 3,
    tags: [FlowItemParameterTag.STITCH],
    ui: {
      widget: WidgetType.SLIDER,
      widgetOptions: {
        step: 1,
      },
    },
    value: {
      type: 'number',
      default: 2,
      minimum: 2,
      maximum: 50,
      required: true,
    },
  };

  export const TackHoldMetadata: FlowItemParameterMetadata = {
    ref: FlowItemParameterRef.TACK_HOLD,
    apiName: 'tackHoldTime',
    name: 'welding:tack_hold_time',
    tags: [FlowItemParameterTag.TACK],
    displayOrder: 550,
    ui: {
      widget: WidgetType.SLIDER,
      widgetOptions: {
        step: 0.1,
      },
    },
    value: {
      type: 'number',
      default: 0.5,
      minimum: 0.1,
      maximum: 3,
      uom: {
        target: 's',
      },
    },
  };

  export const QNearMetadata: FlowItemParameterMetadata = {
    ref: FlowItemParameterRef.QNEAR,
    apiName: 'qnear',
    tags: [
      FlowItemParameterTag.PROCESS_CV,
      FlowItemParameterTag.PROCESS_PULSE,
      FlowItemParameterTag.PROCESS_OTC_WAVE_PULSE,
      FlowItemParameterTag.PROCESS_MILLER_RMD,
      FlowItemParameterTag.PROCESS_MILLER_HD,
    ],
    value: {
      type: 'array',
      required: true,
    },
  };

  export const WaypointParameterValue: ParameterValue = {
    type: 'object',
    required: true,
    props: {
      x: {
        type: 'number',
        required: true,
      },
      y: {
        type: 'number',
        required: true,
      },
      z: {
        type: 'number',
        required: true,
      },
      rx: {
        type: 'number',
        required: true,
      },
      ry: {
        type: 'number',
        required: true,
      },
      rz: {
        type: 'number',
        required: true,
      },
    },
  };

  export const WaypointMetadata: FlowItemParameterMetadata = {
    ref: FlowItemParameterRef.WAYPOINT,
    apiName: 'waypoint',
    name: 'welding:waypoint',
    tags: [
      FlowItemParameterTag.PROCESS_CV,
      FlowItemParameterTag.PROCESS_PULSE,
      FlowItemParameterTag.PROCESS_OTC_WAVE_PULSE,
      FlowItemParameterTag.PROCESS_MILLER_RMD,
      FlowItemParameterTag.PROCESS_MILLER_HD,
    ],
    value: WaypointParameterValue,
  };

  export const WeldTypeMetadata: FlowItemParameterMetadata = {
    ref: FlowItemParameterRef.WELD_TYPE,
    apiName: 'motionType',
    name: 'welding:weld_type',
    tags: [
      FlowItemParameterTag.PROCESS_CV,
      FlowItemParameterTag.PROCESS_PULSE,
      FlowItemParameterTag.PROCESS_MILLER_RMD,
      FlowItemParameterTag.PROCESS_MILLER_HD,
      FlowItemParameterRef.APPROACH,
      FlowItemParameterRef.DEPART,
    ],
    description: 'welding:weld_type_desc',
    displayOrder: 1,
    ui: {
      widget: WidgetType.PICKER,
      widgetOptions: [
        {
          label: 'welding:linear',
          value: WeldTypeEnum.LINEAR,
        },
        {
          label: 'welding:radial',
          value: WeldTypeEnum.RADIAL,
        },
        {
          label: 'welding:tack',
          value: WeldTypeEnum.TACK,
        },
        {
          label: 'welding:linear_stitch',
          value: WeldTypeEnum.LINEAR_STITCH,
        },
        {
          label: 'welding:rotary',
          value: WeldTypeEnum.ROTARY,
        },
      ],
    },
    value: {
      type: 'number',
      default: 1,
    },
  };

  export const MoveMotionTypeMetadata: FlowItemParameterMetadata = {
    ref: FlowItemParameterRef.MOVE_MOTION_TYPE,
    apiName: 'motionType',
    name: 'welding:motion_type',
    value: {
      type: 'number',
      default: 0,
    },
    displayOrder: 1,
    ui: {
      widget: WidgetType.HIDDEN,
    },
  };

  export const MoveSpeedAdjustmentRatioMetadata: FlowItemParameterMetadata = {
    ref: FlowItemParameterRef.MOVE_SPEED_ADJUSTMENT_RATIO,
    apiName: 'moveSpeedAdjustmentRatio',
    name: 'welding:move_speed_adjustment_ratio',
    displayOrder: 10,
    description: 'welding:move_speed_adjustment_ratio_desc',
    ui: {
      widget: WidgetType.SLIDER,
      widgetOptions: {
        step: 0.25,
      },
    },
    value: {
      type: 'number',
      required: true,
      minimum: 0.25,
      maximum: 1,
      default: 1,
    },
  };

  export const MoveTravelSpeedMetadata: FlowItemParameterMetadata = {
    ref: FlowItemParameterRef.MOVE_TRAVEL_SPEED,
    apiName: 'travelSpeed',
    name: 'welding:travel_speed',
    value: {
      type: 'number',
      default: 0.03,
    },
  };

  export const MoveBlendRadiusMetadata: FlowItemParameterMetadata = {
    ref: FlowItemParameterRef.MOVE_BLEND_RADIUS,
    apiName: 'blendRadius',
    displayOrder: 10,
    name: 'welding:blend_radius',
    value: {
      type: 'number',
      default: 50,
      minimum: 0,
      maximum: 100,
      required: true,
    },
    ui: {
      widget: WidgetType.HIDDEN,
    },
  };

  export const ProductionQuanityMetadata: FlowItemParameterMetadata = {
    ref: FlowItemParameterRef.PRODUCTION_QUANTITY,
    apiName: 'quantity',
    displayOrder: 0,
    name: 'welding:quantity',
    value: {
      type: 'number',
      default: 1,
      required: true,
    },
    ui: {
      widget: WidgetType.NUMBER,
    },
  };

  export const TriggerOutputPortMetadata: FlowItemParameterMetadata = {
    ref: FlowItemParameterRef.TRIGGER_OUTPUT_PORT,
    apiName: 'outputPort',
    displayOrder: 0,
    name: 'welding:trigger_output',
    value: {
      type: 'number',
      default: 24,
      required: true,
    },
    ui: {
      widget: WidgetType.PICKER,
      widgetOptions: [
        {
          label: 'welding:red',
          value: 24,
        },
        {
          label: 'welding:blue',
          value: 25,
        },
        {
          label: 'welding:yellow',
          value: 26,
        },
        {
          label: 'welding:green',
          value: 27,
        },
      ],
    },
  };

  export const TriggerOutputStateMetadata: FlowItemParameterMetadata = {
    ref: FlowItemParameterRef.TRIGGER_OUTPUT_STATE,
    apiName: 'outputState',
    displayOrder: 0,
    name: 'welding:trigger_state',
    value: {
      type: 'boolean',
      default: true,
    },
    ui: {
      widget: WidgetType.PICKER,
      widgetOptions: [
        {
          label: 'welding:on',
          value: true,
        },
        {
          label: 'welding:off',
          value: false,
        },
      ],
    },
  };

  export const RotaryTriggerOutputPortMetadata: FlowItemParameterMetadata =
    assign({}, TriggerOutputPortMetadata, {
      tags: [FlowItemParameterTag.ROTARY],
      displayOrder: 1.1,
    } as FlowItemParameterMetadata);

  export const WaitForOperatorMessageMetadata: FlowItemParameterMetadata = {
    ref: FlowItemParameterRef.WAIT_FOR_OPERATOR_MESSAGE,
    apiName: 'message',
    tags: [FlowItemParameterTag.WAIT_FOR_OPERATOR],
    displayOrder: 100,
    name: 'welding:wait_for_message',
    value: {
      type: 'string',
      default: 'Please press the blue light push button to continue.',
      required: false,
    },
    ui: {
      widget: WidgetType.TEXT,
    },
  };

  export enum WaitForInputType {
    OPERATOR_BUTTON = 'OPERATOR_BUTTON',
    SENSOR = 'SENSOR',
    TIME = 'TIME',
  }

  export enum WaitForInputPort {
    RED = 16,
    BLUE = 17,
    YELLOW = 18,
    GREEN = 19,
  }

  export const WaitForInputPortValueToName: Record<
    WaitForInputPort,
    i18nKeyType
  > = {
    16: 'welding:red',
    17: 'welding:blue',
    18: 'welding:yellow',
    19: 'welding:green',
  };

  export const WaitForTypeMetadata: FlowItemParameterMetadata = {
    ref: FlowItemParameterRef.WAIT_FOR_TYPE_OF_INPUT,
    apiName: 'typeOfInput',
    displayOrder: 0,
    name: 'welding:wait_for',
    value: {
      type: 'string',
      default: WaitForInputType.OPERATOR_BUTTON,
      required: false,
    },
    ui: {
      widget: WidgetType.PICKER,
      widgetOptions: [
        {
          label: 'welding:operator',
          value: WaitForInputType.OPERATOR_BUTTON,
        },
        {
          label: 'welding:input',
          value: WaitForInputType.SENSOR,
        },
        {
          label: 'welding:time',
          value: WaitForInputType.TIME,
        },
      ],
    },
  };

  export const WaitForInputMetadata: FlowItemParameterMetadata = {
    ref: FlowItemParameterRef.WAIT_FOR_INPUT,
    tags: [FlowItemParameterTag.WAIT_FOR_INPUT],
    apiName: 'inputPort',
    displayOrder: 0,
    name: 'welding:wait_for_input',
    value: {
      type: 'number',
      default: WaitForInputPort.RED,
      required: true,
    },
    ui: {
      widget: WidgetType.PICKER,
      widgetOptions: [
        {
          label: WaitForInputPortValueToName[WaitForInputPort.RED],
          value: WaitForInputPort.RED,
        },
        {
          label: WaitForInputPortValueToName[WaitForInputPort.BLUE],
          value: WaitForInputPort.BLUE,
        },
        {
          label: WaitForInputPortValueToName[WaitForInputPort.YELLOW],
          value: WaitForInputPort.YELLOW,
        },
        {
          label: WaitForInputPortValueToName[WaitForInputPort.GREEN],
          value: WaitForInputPort.GREEN,
        },
      ],
    },
  };

  export const WaitForInputStateMetadata: FlowItemParameterMetadata = {
    ref: FlowItemParameterRef.WAIT_FOR_INPUT_STATE,
    tags: [FlowItemParameterTag.WAIT_FOR_INPUT],
    apiName: 'waitForState',
    displayOrder: 0,
    name: 'welding:wait_for_input_state',
    value: {
      type: 'boolean',
      default: true,
    },
    ui: {
      widget: WidgetType.PICKER,
      widgetOptions: [
        {
          label: 'welding:on',
          value: true,
        },
        {
          label: 'welding:off',
          value: false,
        },
      ],
    },
  };

  export const WaitForTimeMetadata: FlowItemParameterMetadata = {
    ref: FlowItemParameterRef.WAIT_FOR_TIME,
    apiName: 'inputPort',
    tags: [FlowItemParameterTag.WAIT_FOR_TIME],
    displayOrder: 0,
    name: 'welding:wait_for_time',
    value: {
      type: 'number',
      default: 5,
    },
    ui: {
      widget: WidgetType.NUMBER,
      widgetOptions: {
        step: 0.01,
      },
    },
  };

  export const RotaryWaitForTypeMetadata: FlowItemParameterMetadata = assign(
    {},
    WaitForTypeMetadata,
    {
      tags: [FlowItemParameterTag.ROTARY],
      displayOrder: 1.01,
      value: {
        ...WaitForTypeMetadata.value,
        default: WaitForInputType.SENSOR,
      },
      ui: {
        ...WaitForTypeMetadata.ui,
        widgetOptions: (
          WaitForTypeMetadata.ui as PickerWidget
        )?.widgetOptions.filter(
          (opt) => opt.value !== WaitForInputType.OPERATOR_BUTTON
        ),
      },
    } as FlowItemParameterMetadata
  );

  export const RotaryWaitForInputMetadata: FlowItemParameterMetadata = assign(
    {},
    WaitForInputMetadata,
    {
      displayOrder: 1.02,
    } as FlowItemParameterMetadata
  );

  export const RotaryWaitForInputStateMetadata: FlowItemParameterMetadata =
    assign({}, WaitForInputStateMetadata, {
      displayOrder: 1.03,
    } as FlowItemParameterMetadata);

  export const RotaryWaitForTimeMetadata: FlowItemParameterMetadata = assign(
    {},
    WaitForTimeMetadata,
    {
      displayOrder: 1.04,
    } as FlowItemParameterMetadata
  );

  export const SeamTrackingEnabledMetadata: FlowItemParameterMetadata = {
    ref: FlowItemParameterRef.SEAM_TRACKING_ENABLED,
    apiName: 'seamTrackingEnabled',
    displayOrder: 990,
    tags: [
      FlowItemParameterTag.PROCESS_CV,
      FlowItemParameterTag.PROCESS_PULSE,
      FlowItemParameterTag.PROCESS_OTC_WAVE_PULSE,
      FlowItemParameterTag.PROCESS_MILLER_RMD,
      FlowItemParameterTag.PROCESS_MILLER_HD,
    ],
    name: 'welding:seam_tracking_enabled',
    value: {
      type: 'boolean',
      default: false,
    },
    ui: {
      widget: WidgetType.SWITCH,
    },
    featureFlag: 'beacon-welding-seam-tracking',
    paywallOptions: {
      planEntitlementKey: PlanEntitlementKey.WELDING_SEAM_TRACKING,
    },
  };

  export const SeamTrackingLateralEnabledMetadata: FlowItemParameterMetadata = {
    ref: FlowItemParameterRef.SEAM_TRACKING_LATERAL_ENABLED,
    apiName: 'seamTrackingLateralEnabled',
    displayOrder: 991,
    name: 'welding:seam_tracking_lateral_enabled',
    value: {
      type: 'boolean',
      default: true,
    },
    ui: {
      widget: WidgetType.SWITCH,
    },
    tagCondition: (_tags, form) => {
      return getSeamTrackingEnabledFromForm(form);
    },
  };

  export const LateralTrackRightMetadata: FlowItemParameterMetadata = {
    ref: FlowItemParameterRef.SEAM_TRACKING_LATERAL_TRACK_RIGHT,
    apiName: 'lateralTrackRight',
    displayOrder: 992,
    name: 'welding:lateral_track_right',
    value: {
      type: 'boolean',
      default: true,
    },
    ui: {
      widget: WidgetType.SWITCH,
    },
    tagCondition: (_tags, form) => {
      const seamTrackingEnabled = getSeamTrackingEnabledFromForm(form);
      const lateralEnabled = getLateralEnabledFromForm(form);
      return seamTrackingEnabled && lateralEnabled;
    },
  };

  export const LateralTrackLeftMetadata: FlowItemParameterMetadata = {
    ref: FlowItemParameterRef.SEAM_TRACKING_LATERAL_TRACK_LEFT,
    apiName: 'lateralTrackLeft',
    displayOrder: 993,
    name: 'welding:lateral_track_left',
    value: {
      type: 'boolean',
      default: true,
    },
    ui: {
      widget: WidgetType.SWITCH,
    },
    tagCondition: (_tags, form) => {
      const seamTrackingEnabled = getSeamTrackingEnabledFromForm(form);
      const lateralEnabled = getLateralEnabledFromForm(form);
      return seamTrackingEnabled && lateralEnabled;
    },
  };

  export const LateralSensitivityMetadata: FlowItemParameterMetadata = {
    ref: FlowItemParameterRef.SEAM_TRACKING_LATERAL_SENSITIVITY,
    apiName: 'lateralSensitivity',
    displayOrder: 994,
    name: 'welding:lateral_sensitivity_ratio',
    value: {
      type: 'number',
      minimum: 0,
      maximum: 1,
      default: 0.5,
    },
    ui: {
      widget: WidgetType.SLIDER,
      widgetOptions: {
        step: 0.25,
      },
    },
    tagCondition: (_tags, form) => {
      const seamTrackingEnabled = getSeamTrackingEnabledFromForm(form);
      const lateralEnabled = getLateralEnabledFromForm(form);
      return seamTrackingEnabled && lateralEnabled;
    },
  };

  export const SeamTrackingVerticalEnabledMetadata: FlowItemParameterMetadata =
    {
      ref: FlowItemParameterRef.SEAM_TRACKING_VERTICAL_ENABLED,
      apiName: 'seamTrackingVerticalEnabled',
      displayOrder: 995,
      name: 'welding:seam_tracking_vertical_enabled',
      value: {
        type: 'boolean',
        default: true,
      },
      ui: {
        widget: WidgetType.SWITCH,
      },
      tagCondition: (_tags, form) => {
        return getSeamTrackingEnabledFromForm(form);
      },
    };

  export const HeightSensitivityMetadata: FlowItemParameterMetadata = {
    ref: FlowItemParameterRef.SEAM_TRACKING_HEIGHT_SENSITIVITY,
    apiName: 'heightSensitivity',
    displayOrder: 996,
    name: 'welding:vertical_sensitivity_ratio',
    value: {
      type: 'number',
      minimum: 0,
      maximum: 1,
      default: 0.5,
    },
    ui: {
      widget: WidgetType.SLIDER,
      widgetOptions: {
        step: 0.25,
      },
    },
    tagCondition: (_tags, form) => {
      const verticalEnabled =
        getParameterValueFromForm<boolean>(
          form,
          FlowItemParameterRef.SEAM_TRACKING_VERTICAL_ENABLED
        ) ?? false;
      return getSeamTrackingEnabledFromForm(form) && verticalEnabled;
    },
  };

  export const WeaveEnabledMetadata: FlowItemParameterMetadata = {
    ref: FlowItemParameterRef.WEAVE_ENABLED,
    apiName: 'weaveActive',
    displayOrder: 1000,
    tags: [
      FlowItemParameterTag.PROCESS_PULSE,
      FlowItemParameterTag.PROCESS_CV,
      FlowItemParameterTag.PROCESS_OTC_WAVE_PULSE,
      FlowItemParameterTag.PROCESS_MILLER_RMD,
      FlowItemParameterTag.PROCESS_MILLER_HD,
    ],
    name: 'welding:weave',
    value: {
      type: 'boolean',
      default: false,
    },
    ui: {
      widget: WidgetType.SWITCH,
    },
    calculateValue: (_config, item, currentValue) => {
      const seamTrackingEnabled =
        getParameterValue<boolean>(
          item,
          FlowItemParameterRef.SEAM_TRACKING_ENABLED
        ) ?? false;

      const enabled = seamTrackingEnabled ? true : currentValue;

      return enabled;
    },
    readonlyCondition: (form) => {
      const seamTrackingEnabled =
        getParameterValueFromForm<boolean>(
          form,
          FlowItemParameterRef.SEAM_TRACKING_ENABLED
        ) ?? false;

      return seamTrackingEnabled;
    },
    readonlyHint: 'welding:weave_enabled_readonly_hint',
  };

  export const WeavePatternMetadata: FlowItemParameterMetadata = {
    ref: FlowItemParameterRef.WEAVE_PATTERN,
    apiName: 'weavePattern',
    displayOrder: 1001,
    name: 'welding:weave_pattern',
    value: {
      type: 'number',
      default: 1,
    },
    ui: {
      widget: WidgetType.PICKER,
      widgetOptions: [
        {
          label: 'welding:weave_pattern_triangle',
          value: WeavePatternEnum.TRIANGLE,
        },
        {
          label: 'welding:weave_pattern_whip_stitch',
          value: WeavePatternEnum.WHIP_STITCH,
        },
      ],
    },
    tags: [FlowItemParameterTag.WEAVE],
    tagCondition: (_tags, form) => {
      const weaveEnabled =
        getParameterValueFromForm<boolean>(
          form,
          FlowItemParameterRef.WEAVE_ENABLED
        ) ?? false;

      // Hide weave pattern when seam tracking is enabled, show when weave is enabled without seam tracking
      return weaveEnabled;
    },
  };

  export const WeaveLegLength1Metadata: FlowItemParameterMetadata = {
    ref: FlowItemParameterRef.WEAVE_PATH_OFFSET_1,
    apiName: 'pathOffset1',
    displayOrder: 1002,
    name: 'welding:weave_path_offset_1',
    value: {
      type: 'number',
      minimum: 0,
      maximum: 20,
      default: 1,
      uom: {
        target: 'mm',
      },
    },
    ui: {
      widget: WidgetType.SLIDER,
      widgetOptions: {
        step: 1,
      },
    },
    tags: [FlowItemParameterTag.WEAVE],
    tagCondition: (_tags, form) => {
      const weaveEnabled =
        getParameterValueFromForm<boolean>(
          form,
          FlowItemParameterRef.WEAVE_ENABLED
        ) ?? false;

      // Hide weave parameters when seam tracking is enabled
      return weaveEnabled;
    },
  };

  export const WeaveLegLength2Metadata: FlowItemParameterMetadata = {
    ref: FlowItemParameterRef.WEAVE_PATH_OFFSET_2,
    apiName: 'pathOffset2',
    displayOrder: 1003,
    name: 'welding:weave_path_offset_2',
    value: {
      type: 'number',
      minimum: 0,
      maximum: 20,
      default: 1,
      uom: {
        target: 'mm',
      },
    },
    ui: {
      widget: WidgetType.SLIDER,
      widgetOptions: {
        step: 1,
      },
    },
    tags: [FlowItemParameterTag.WEAVE],
    tagCondition: (_tags, form) => {
      const weaveEnabled =
        getParameterValueFromForm<boolean>(
          form,
          FlowItemParameterRef.WEAVE_ENABLED
        ) ?? false;

      // Hide weave parameters when seam tracking is enabled
      return weaveEnabled;
    },
  };

  export const WeaveFrequencyMetadata: FlowItemParameterMetadata = {
    ref: FlowItemParameterRef.WEAVE_FREQUENCY,
    apiName: 'weaveFrequency',
    displayOrder: 1004,
    name: 'welding:weave_freq',
    value: {
      type: 'number',
      minimum: 0,
      maximum: 5,
      default: 2,
      uom: {
        target: 'Hz',
      },
    },
    ui: {
      widget: WidgetType.SLIDER,
      widgetOptions: {
        step: 0.25,
      },
    },
    tags: [FlowItemParameterTag.WEAVE],
    tagCondition: (_tags, form) => {
      const weaveEnabled =
        getParameterValueFromForm<boolean>(
          form,
          FlowItemParameterRef.WEAVE_ENABLED
        ) ?? false;

      // Hide weave parameters when seam tracking is enabled
      return weaveEnabled;
    },
  };

  export const WeaveDwellTime1Metadata: FlowItemParameterMetadata = {
    ref: FlowItemParameterRef.WEAVE_DWELL_TIME_1,
    apiName: 'dwellTime1',
    displayOrder: 1005,
    name: 'welding:weave_dwell_time_1',
    value: {
      type: 'number',
      minimum: 0,
      maximum: 1,
      default: 0,
      uom: {
        target: 's',
      },
    },
    ui: {
      widget: WidgetType.SLIDER,
      widgetOptions: {
        step: 0.1,
      },
    },
    tagCondition: (_tags, form) => {
      const weaveEnabled =
        getParameterValueFromForm<boolean>(
          form,
          FlowItemParameterRef.WEAVE_ENABLED
        ) ?? false;

      // Always show dwell time fields when weave is enabled, regardless of pattern
      return weaveEnabled;
    },
  };

  export const WeaveDwellTime2Metadata: FlowItemParameterMetadata = {
    ref: FlowItemParameterRef.WEAVE_DWELL_TIME_2,
    apiName: 'dwellTime2',
    displayOrder: 1006,
    name: 'welding:weave_dwell_time_2',
    value: {
      type: 'number',
      minimum: 0,
      maximum: 1,
      default: 0,
      uom: {
        target: 's',
      },
    },
    ui: {
      widget: WidgetType.SLIDER,
      widgetOptions: {
        step: 0.1,
      },
    },
    // reuse tag condition from dwell time 1 since they follow the same logic
    tagCondition: WeaveDwellTime1Metadata.tagCondition,
  };

  export const SectionColorParameterMetadata: FlowItemParameterMetadata = {
    ref: FlowItemParameterRef.SECTION_COLOR,
    name: 'welding:color',
    displayOrder: 10,
    apiName: 'color',
    value: {
      type: 'string',
    },
    ui: {
      widget: WidgetType.COLOR_PICKER,
    },
  };

  export const OTCProcessMetadata: FlowItemParameterMetadata = {
    ref: FlowItemParameterRef.PROCESS,
    apiName: 'process',
    name: 'welding:welding_process',
    tags: [
      FlowItemParameterTag.PROCESS_CV,
      FlowItemParameterTag.PROCESS_PULSE,
      FlowItemParameterTag.PROCESS_OTC_WAVE_PULSE,
      FlowItemParameterRef.APPROACH,
    ],
    displayOrder: 2,
    ui: {
      widget: WidgetType.PICKER,
      widgetOptions: [
        {
          label: 'welding:pulse',
          value: 'pulse',
        },
        {
          label: 'welding:wave_pulse',
          value: 'wave_pulse',
        },
        {
          label: 'welding:cv',
          value: 'cv',
        },
      ],
    },
    value: {
      type: 'string',
      default: 'pulse',
    },
  };

  export const OTCVoltageMetadata: FlowItemParameterMetadata = {
    ref: FlowItemParameterRef.VOLTAGE,
    apiName: 'voltage',
    name: 'welding:voltage',
    description: 'welding:voltage_desc',
    displayOrder: 16,
    ui: {
      widget: WidgetType.NUMBER,
      widgetOptions: {
        step: 0.1,
      },
    },
    value: {
      type: 'number',
      required: true,
      minimum: 12,
      maximum: 45,
    },
    tagCondition: (tags, form) => {
      const singleItemWeld = isSingleItemWeldFromForm(form);

      // check the depart flag on the form instead of the depart tag since
      // we want this to show in the weld parameter settings form, but not
      // on actual depart steps. the weld parameter settings form forces
      // a depart flag = true so we can configure more parameters when creating
      // a new part
      const isDepart = getParameterValueFromForm(
        form,
        FlowItemParameterRef.DEPART
      );

      if (isDepart && !singleItemWeld) {
        return false;
      }

      if (tags.includes(FlowItemParameterTag.PROCESS_CV)) {
        return true;
      } else {
        return !tags.includes(FlowItemParameterTag.SYNERGIC_MODE);
      }
    },
  };

  type OTCPenetrationEnabledCondition = {
    process: string[];
    gas: string[];
    materialType: string[];
    wireType: string[];
    wireDiameter: number[];
    travelSpeed: number; // TODO: figure out how to determine the requirement...might be a function that takes in the number?
  };

  export const OTCPenetrationRequirements: OTCPenetrationEnabledCondition[] = [
    {
      process: [WeldProcess.CV],
      gas: [Gas.GAS_100CO2],
      materialType: [Material.STEEL],
      wireType: [WireType.SOLID],
      wireDiameter: [0.03, 0.035, 0.04, 0.045],
      travelSpeed: 0,
    },
    {
      process: [WeldProcess.CV],
      gas: CO2_20,
      materialType: [Material.STEEL],
      wireType: [WireType.SOLID],
      wireDiameter: [0.03, 0.035, 0.04, 0.045],
      travelSpeed: 0,
    },
    {
      process: [WeldProcess.CV],
      gas: CO2_20,
      materialType: [Material.STEEL, Material.STAINLESS_STEEL],
      wireType: [WireType.METAL, WireType.FLUX],
      wireDiameter: [0.045],
      travelSpeed: 0,
    },
    {
      process: [WeldProcess.CV],
      gas: O2_2,
      materialType: [
        Material.STAINLESS_STEEL,
        Material.STAINLESS_STEEL_FERRITE,
      ],
      wireType: [WireType.SOLID],
      wireDiameter: [0.03, 0.035, 0.04, 0.045],
      travelSpeed: 0,
    },
    {
      process: [WeldProcess.PULSE],
      gas: CO2_20,
      materialType: [Material.STEEL],
      wireType: [WireType.SOLID],
      wireDiameter: [0.03, 0.035, 0.04, 0.045, 0.052],
      travelSpeed: 0,
    },
    {
      process: [WeldProcess.PULSE],
      gas: CO2_20,
      materialType: [Material.STEEL],
      wireType: [WireType.METAL],
      wireDiameter: [0.045],
      travelSpeed: 0,
    },
    {
      process: [WeldProcess.PULSE],
      gas: O2_2,
      materialType: [
        Material.STAINLESS_STEEL,
        Material.STAINLESS_STEEL_FERRITE,
      ],
      wireType: [WireType.SOLID],
      wireDiameter: [0.03, 0.035, 0.045],
      travelSpeed: 0,
    },
  ];

  export const OTCPenetrationControlMetadata: FlowItemParameterMetadata<
    FlowItemParameterTag,
    FlowConfig
  > = {
    ref: FlowItemParameterRef.PENETRATION_CONTROL,
    apiName: 'penetrationControl',
    name: 'welding:penetration_control',
    displayOrder: 900,
    ui: {
      widget: WidgetType.HIDDEN,
    },
    value: {
      type: 'boolean',
      default: false,
    },
    calculateValue: (config: FlowConfig, item: FlowItem): boolean => {
      const process = getParameterValue<string>(
        item,
        FlowItemParameterRef.PROCESS
      );

      // TODO: Need to account for travel speed and check what the stainless steel (*3) is
      const _travelSpeed = getParameterValue<number>(
        item,
        FlowItemParameterRef.TRAVEL_SPEED
      );

      const matchedRequirements = OTCPenetrationRequirements.find(
        (requirements) => {
          const hasGas = config.gas
            ? requirements.gas.includes(config.gas)
            : true;
          const hasWireType = config.wireType
            ? requirements.wireType.includes(config.wireType)
            : true;

          return (
            requirements.process.includes(process) &&
            requirements.materialType.includes(config.materialType) &&
            requirements.wireDiameter.includes(config.wireDiameter) &&
            hasGas &&
            hasWireType
            // TODO: figure out what this should be
            // && travelSpeed > requirements.travelSpeed
          );
        }
      );

      return matchedRequirements !== undefined;
    },
  };

  export const OTCSynergicMode: FlowItemParameterMetadata = {
    ref: FlowItemParameterRef.SYNERGIC_MODE,
    apiName: 'voltageSynergySetting',
    name: 'welding:synergic_mode',
    tags: [
      FlowItemParameterTag.PROCESS_PULSE,
      FlowItemParameterTag.PROCESS_OTC_WAVE_PULSE,
    ],
    displayOrder: 14,
    ui: {
      widget: WidgetType.SWITCH,
    },
    value: {
      type: 'boolean',
      default: true,
    },
    calculateValue: (
      _config: FlowConfig,
      item: FlowItem,
      currentValue: boolean
    ) => {
      const process = getParameterValue(item, FlowItemParameterRef.PROCESS);

      let returnVal = currentValue;

      if (process === WeldProcess.CV) {
        returnVal = false;
      }

      return returnVal;
    },
  };

  export const OTCSynergicModeFineAdjustment: FlowItemParameterMetadata = {
    ref: FlowItemParameterRef.SYNERGIC_MODE_FINE_ADJUSTMENT,
    apiName: 'voltageSynergyFineAdjustment',
    name: 'welding:synergic_mode_fine_adjustment',
    tagCondition: (tags: string[]) => {
      return (
        !tags.includes(FlowItemParameterTag.PROCESS_CV) &&
        tags.includes(FlowItemParameterTag.SYNERGIC_MODE)
      );
    },
    displayOrder: 15,
    ui: {
      widget: WidgetType.SLIDER,
      widgetOptions: {
        step: 1,
      },
    },
    value: {
      type: 'number',
      default: 0,
      maximum: 100,
      minimum: -100,
    },
  };

  export const OTCWaveFrequency: FlowItemParameterMetadata = {
    ref: FlowItemParameterRef.WAVE_FREQUENCY,
    apiName: 'waveFrequency',
    name: 'welding:wave_frequency',
    tags: [FlowItemParameterTag.PROCESS_OTC_WAVE_PULSE],
    displayOrder: 13,
    ui: {
      widget: WidgetType.NUMBER,
      widgetOptions: {
        step: 0.1,
      },
    },
    value: {
      type: 'number',
      default: 3,
      maximum: 32,
      minimum: 0.5,
      uom: {
        target: 'Hz',
      },
    },
  };

  export const OTCArcCharacteristic: FlowItemParameterMetadata = {
    ref: FlowItemParameterRef.ARC_CHARACTERISTIC,
    apiName: 'arcCharacteristic',
    name: 'welding:arc_characteristic',
    tags: [
      FlowItemParameterTag.PROCESS_CV,
      FlowItemParameterTag.PROCESS_PULSE,
      FlowItemParameterTag.PROCESS_OTC_WAVE_PULSE,
    ],
    displayOrder: 901,
    ui: {
      widget: WidgetType.NUMBER,
    },
    value: {
      type: 'number',
      default: 0,
      maximum: 100,
      minimum: -100,
    },
  };

  export const OTCWeldingCurrent: FlowItemParameterMetadata = {
    ref: FlowItemParameterRef.WELDING_CURRENT,
    apiName: 'weldingCurrent',
    name: 'welding:welding_current',
    tags: [
      FlowItemParameterTag.PROCESS_CV,
      FlowItemParameterTag.PROCESS_PULSE,
      FlowItemParameterTag.PROCESS_OTC_WAVE_PULSE,
    ],
    tagCondition: (tags: string[], form: ParametersForm) => {
      const singleItemWeld = isSingleItemWeldFromForm(form);

      // check the depart flag on the form instead of the depart tag since
      // we want this to show in the weld parameter settings form, but not
      // on actual depart steps. the weld parameter settings form forces
      // a depart flag = true so we can configure more parameters when creating
      // a new part
      const isDepart = getParameterValueFromForm(
        form,
        FlowItemParameterRef.DEPART
      );

      if (isDepart && !singleItemWeld) {
        return false;
      }

      return !tags.includes(FlowItemParameterTag.USE_WIRE_FEED_SPEED);
    },
    displayOrder: 19,
    ui: {
      widget: WidgetType.NUMBER,
      widgetOptions: {
        step: 1,
      },
    },
    value: {
      type: 'number',
      maximum: 400,
      minimum: 20,
      required: true,
    },
  };

  export const OTCUseWireFeedSpeed: FlowItemParameterMetadata = {
    ref: FlowItemParameterRef.USE_WIRE_FEED_SPEED,
    apiName: 'wireSpeedSetting',
    name: 'welding:use_wire_feed_speed',
    tags: [
      FlowItemParameterTag.PROCESS_CV,
      FlowItemParameterTag.PROCESS_PULSE,
      FlowItemParameterTag.PROCESS_OTC_WAVE_PULSE,
    ],
    displayOrder: 18,
    ui: {
      widget: WidgetType.SWITCH,
    },
    value: {
      type: 'boolean',
      default: true,
    },
  };

  export const OTCWireFeedSpeed: FlowItemParameterMetadata = {
    ref: FlowItemParameterRef.WIRE_FEED_SPEED,
    apiName: 'wireFeedSpeed',
    name: 'welding:wire_feed_speed',
    description: 'welding:wire_feed_speed_desc',
    tags: [FlowItemParameterTag.USE_WIRE_FEED_SPEED],
    displayOrder: 20,
    value: {
      type: 'number',
      required: true,
      minimum: 15,
      maximum: 705,
      uom: {
        target: 'in/min',
        system: {
          imperial: 'in/min',
          metric: 'm/min',
        },
        precision: 3,
      },
    },
    ui: {
      widget: WidgetType.NUMBER,
    },
  };

  export const ESABTrimMetadata: FlowItemParameterMetadata = {
    ref: FlowItemParameterRef.ARC_LENGTH,
    apiName: 'arcLength',
    name: 'welding:trim',
    tags: [FlowItemParameterTag.PROCESS_PULSE],
    displayOrder: 10,
    ui: {
      widget: WidgetType.SLIDER,
      widgetOptions: {
        step: 1,
      },
    },
    value: {
      type: 'number',
      default: 0,
      maximum: 10,
      minimum: -10,
    },
  };

  export const ESABWireFeedSpeedMetadata: FlowItemParameterMetadata = {
    ref: FlowItemParameterRef.WIRE_FEED_SPEED,
    apiName: 'wireFeedSpeed',
    name: 'welding:wire_feed_speed',
    description: 'welding:wire_feed_speed_desc',
    tags: [FlowItemParameterTag.PROCESS_CV, FlowItemParameterTag.PROCESS_PULSE],
    displayOrder: 20,
    value: {
      type: 'number',
      required: true,
      minimum: 60,
      maximum: 980,
      uom: {
        target: 'in/min',
        system: {
          imperial: 'in/min',
          metric: 'm/min',
        },
        precision: 3,
      },
    },
    ui: {
      widget: WidgetType.NUMBER,
      widgetOptions: {
        step: 0.01,
      },
    },
  };

  export const ESABVoltageMetadata: FlowItemParameterMetadata = {
    ref: FlowItemParameterRef.VOLTAGE,
    apiName: 'voltage',
    name: 'welding:voltage',
    description: 'welding:voltage_desc',
    tags: [FlowItemParameterTag.PROCESS_CV],
    displayOrder: 30,
    ui: {
      widget: WidgetType.NUMBER,
      widgetOptions: {
        step: 0.1,
      },
    },
    value: {
      type: 'number',
      required: true,
      minimum: 8,
      maximum: 60,
    },
  };

  export const MillerDeltaWeldProcessMetadata: FlowItemParameterMetadata = {
    ref: FlowItemParameterRef.PROCESS,
    apiName: 'process',
    name: 'welding:welding_process',
    tags: [
      FlowItemParameterTag.PROCESS_CV,
      FlowItemParameterTag.PROCESS_PULSE,
      FlowItemParameterTag.PROCESS_MILLER_RMD,
      FlowItemParameterTag.PROCESS_MILLER_HD,
      FlowItemParameterRef.APPROACH,
    ],
    displayOrder: 2,
    ui: {
      widget: WidgetType.PICKER,
      widgetOptions: [
        {
          label: 'welding:pulse',
          value: WeldProcess.PULSE,
        },
        {
          label: 'welding:cv',
          value: WeldProcess.CV,
        },
        {
          label: 'welding:process_rmd',
          value: WeldProcess.MILLER_RMD,
        },
        // Hide HD process until we can fully test
        // {
        //   label: 'welding:process_hd',
        //   value: WeldProcess.MILLER_HD,
        // },
      ],
    },
    value: {
      type: 'string',
      default: 'pulse',
    },
  };

  export const MillerDeltaWeldWireFeedSpeedMetadata: FlowItemParameterMetadata =
    {
      ref: FlowItemParameterRef.WIRE_FEED_SPEED,
      apiName: 'wireFeedSpeed',
      name: 'welding:wire_feed_speed',
      description: 'welding:wire_feed_speed_desc',
      tags: [
        FlowItemParameterTag.PROCESS_CV,
        FlowItemParameterTag.PROCESS_PULSE,
        FlowItemParameterTag.PROCESS_MILLER_RMD,
        FlowItemParameterTag.PROCESS_MILLER_HD,
      ],
      displayOrder: 20,
      value: {
        type: 'number',
        required: true,
        minimum: 70,
        maximum: 750,
        uom: {
          target: 'in/min',
          system: {
            imperial: 'in/min',
            metric: 'm/min',
          },
          precision: 3,
        },
      },
      ui: {
        widget: WidgetType.NUMBER,
        widgetOptions: {
          step: 0.01,
        },
      },
      buildValidation: ({
        flow,
        form,
        value,
      }: BuildParameterValidationContext<FlowConfig>) => {
        const process = getParameterValueFromForm<string>(
          form,
          FlowItemParameterRef.PROCESS
        );

        const wireDiameter = flow.config.wireDiameter;

        // TODO noticed wfs for all processes on the new DW were capped to 500

        if (process === WeldProcess.MILLER_RMD) {
          if (wireDiameter <= WireSize['030']) {
            value.maximum = 400;
          } else if (wireDiameter === WireSize['035']) {
            value.maximum = 350;
          } else if (wireDiameter === WireSize['045']) {
            value.maximum = 250;
          } else if (wireDiameter >= WireSize['052']) {
            value.maximum = 200;
          }
        }

        return value;
      },
    };

  export const MillerDeltaWeldArcLengthMetadata: FlowItemParameterMetadata = {
    ref: FlowItemParameterRef.ARC_LENGTH,
    apiName: 'arcLength',
    name: 'welding:arc_length',
    description: 'welding:arc_length_desc',
    tags: [
      FlowItemParameterTag.PROCESS_PULSE,
      FlowItemParameterTag.PROCESS_MILLER_RMD,
    ],
    displayOrder: 30,
    ui: {
      widget: WidgetType.SLIDER,
      widgetOptions: {
        step: 1,
      },
    },
    value: {
      type: 'number',
      default: 0,
      maximum: 10,
      minimum: -10,
    },
  };

  export const MillerDeltaWeldSequencePreflowTimeMetadata: FlowItemParameterMetadata =
    {
      ref: FlowItemParameterRef.SEQUENCE_PREFLOW_TIME,
      apiName: 'preflowTime',
      name: 'welding:preflow_time',
      description: 'welding:preflow_time_desc',
      tags: [FlowItemParameterTag.APPROACH],
      displayOrder: 10.1,
      ui: {
        widget: WidgetType.NUMBER,
        widgetOptions: {
          step: 0.1,
        },
      },
      value: {
        type: 'number',
        default: 0.3,
        minimum: 0,
        maximum: 5,
      },
    };

  export const MillerDeltaWeldSequenceProfilePulseModeMetadata: FlowItemParameterMetadata =
    {
      ref: FlowItemParameterRef.SEQUENCE_PROFILE_PULSE_MODE,
      apiName: 'profilePulseMode',
      name: 'welding:profile_pulse_mode',
      description: 'welding:profile_pulse_mode_desc',
      tags: [
        FlowItemParameterTag.PROCESS_CV,
        FlowItemParameterTag.PROCESS_PULSE,
        FlowItemParameterTag.PROCESS_MILLER_RMD,
        FlowItemParameterTag.PROCESS_MILLER_HD,
      ],
      displayOrder: 10,
      ui: {
        widget: WidgetType.SWITCH,
      },
      value: {
        type: 'boolean',
        default: false,
      },
    };

  export const MillerDeltaWeldSequencePostflowTimeMetadata: FlowItemParameterMetadata =
    {
      ref: FlowItemParameterRef.SEQUENCE_POSTFLOW_TIME,
      apiName: 'postflowTime',
      name: 'welding:postflow_time',
      description: 'welding:postflow_time_desc',
      tags: [FlowItemParameterTag.DEPART],
      displayOrder: 10.2,
      ui: {
        widget: WidgetType.NUMBER,
        widgetOptions: {
          step: 0.1,
        },
      },
      value: {
        type: 'number',
        default: 0.5,
        minimum: 0,
        maximum: 10,
      },
    };

  export const MillerDeltaWeldPresetArcControlMetadata: FlowItemParameterMetadata =
    {
      ref: FlowItemParameterRef.PRESET_ARC_CONTROL,
      apiName: 'arcControl',
      name: 'welding:preset_arc_control',
      description: 'welding:preset_arc_control_desc',
      tags: [
        FlowItemParameterTag.PROCESS_CV,
        FlowItemParameterTag.PROCESS_PULSE,
        FlowItemParameterTag.PROCESS_MILLER_RMD,
        FlowItemParameterTag.PROCESS_MILLER_HD,
      ],
      displayOrder: 10.3,
      ui: {
        widget: WidgetType.NUMBER,
        widgetOptions: {
          step: 1,
        },
      },
      value: {
        type: 'number',
        default: 25,
        minimum: 0,
        maximum: 50,
      },
    };

  export enum MillerDeltaWeldSequenceRunInMode {
    OFF = 0,
    AUTO = 1,
    MANUAL = 2,
  }

  export enum MillerDeltaWeldSequenceStartMode {
    OFF = 0,
    AUTO = 1,
    MANUAL = 2,
  }

  export const MillerDeltaWeldConfigCustomFields: FlowConfig['welder']['customFields'] =
    {
      sequenceRunInMode: MillerDeltaWeldSequenceRunInMode.AUTO,
      sequenceRunInWfs: 30,
      sequenceStartMode: MillerDeltaWeldSequenceStartMode.OFF,
      sequenceStartWfs: 50,
      sequenceStartVoltage: 50,
      sequenceStartArcLength: 50,
      sequenceStartDwellTime: 0.1,
      sequenceStartRampTime: 0.1,
      sequenceBurnBackTime: 30,
      sequenceRetractEnable: true,
      sequenceRetractDistance: 0.1,
    };

  interface ConfigCustomFieldMeta {
    min?: number;
    max?: number;
    uom: PropertyValueUOM;
  }

  export type EquipmentConfigCustomFieldMeta = Record<
    string,
    ConfigCustomFieldMeta
  >;

  export const MillerDeltaWeldConfigCustomFieldUom: Record<
    string,
    ConfigCustomFieldMeta
  > = {
    sequenceRetractDistance: {
      min: 0.1,
      max: 1.5,
      uom: {
        target: 'in',
        system: {
          imperial: 'in',
          metric: 'cm',
        },
        precision: 3,
      },
    },
  };

  export interface GetParameterMetadataFromFormOptions {
    forceWeldType?: WeldTypeEnum;
    forceDepart?: boolean;
    forceApproach?: boolean;
    forceIncludeTags?: FlowItemParameterTag[];
    forceExcludeRefs?: FlowItemParameterRef[];
  }

  export const DefaultWeldParameterSetParameterFormOptions: GetParameterMetadataFromFormOptions =
    {
      forceWeldType: WeldTypeEnum.LINEAR,
      forceIncludeTags: [
        FlowItemParameterTag.DEPART,
        FlowItemParameterTag.APPROACH,
      ],
      forceExcludeRefs: [
        FlowItemParameterRef.WELD_TYPE,
        FlowItemParameterRef.QNEAR,
        FlowItemParameterRef.WAYPOINT,
      ],
      forceApproach: false,
      forceDepart: false,
    };

  export const BeaconFlowItemWeldParametersMetadata = [
    ProcessMetadata,
    ArcOnMetadata,
    ApproachMetadata,
    DepartMetadata,
    WeldTypeMetadata,
    StitchLengthMetadata,
    NumOfStitchesMetadata,
    ArcLengthMetadata,
    ArcStartTimeMetadata,
    CraterFillTimeMetadata,
    StickoutMetadata,
    VoltageMetadata,
    TackHoldMetadata,
    QNearMetadata,
    WaypointMetadata,
    WireFeedSpeedMetadata,
    TravelSpeedMetadata,
    WeaveDwellTime1Metadata,
    WeaveDwellTime2Metadata,
    WeaveEnabledMetadata,
    WeaveLegLength1Metadata,
    WeaveLegLength2Metadata,
    WeaveFrequencyMetadata,
    WeavePatternMetadata,
    RotaryWaitForTypeMetadata,
    RotaryWaitForInputMetadata,
    RotaryWaitForInputStateMetadata,
    RotaryWaitForTimeMetadata,
    RotaryTriggerOutputPortMetadata,
  ];

  export const BeaconFlowItemMoveParametersMetadata: FlowItemParameterMetadata[] =
    [
      MoveBlendRadiusMetadata,
      MoveMotionTypeMetadata,
      MoveSpeedAdjustmentRatioMetadata,
      MoveTravelSpeedMetadata,
      QNearMetadata,
      WaypointMetadata,
    ];

  export const BeaconFlowItemWaitForParametersMetadata: FlowItemParameterMetadata[] =
    [
      WaitForTypeMetadata,
      WaitForInputMetadata,
      WaitForTimeMetadata,
      WaitForOperatorMessageMetadata,
      WaitForInputStateMetadata,
    ];

  export const BeaconFlowItemTriggerOutputParametersMetadata: FlowItemParameterMetadata[] =
    [TriggerOutputPortMetadata, TriggerOutputStateMetadata];

  export const BeaconSectionItemParametersMetadata: FlowItemParameterMetadata[] =
    [SectionColorParameterMetadata];

  export const BeaconStartItem: FlowItemMetadata = {
    ref: FlowItemRef.START,
    codeBlock: 0,
    name: 'welding:start',
    description: 'welding:start_desc',
    icon: home,
    displayOrder: 0,
    availableParameters: [],
  };

  export const BeaconEndItem: FlowItemMetadata = {
    ref: FlowItemRef.END,
    codeBlock: 10000,
    name: 'welding:end',
    description: 'welding:end_desc',
    icon: flag,
    displayOrder: 10000,
    availableParameters: [],
  };

  export const BeaconSectionItem: FlowItemMetadata = {
    ref: FlowItemRef.SECTION,
    codeBlock: 6,
    name: 'welding:section',
    description: 'welding:section_desc',
    icon: flag,
    displayOrder: 10000,
    availableParameters: [...BeaconSectionItemParametersMetadata],
    hidden: true,
  };

  export const BeaconMoveItem: FlowItemMetadata = {
    ref: FlowItemRef.MOVE,
    codeBlock: 2,
    name: 'welding:move',
    description: 'welding:move_desc',
    icon: moveItem,
    displayOrder: 20,
    availableParameters: [...BeaconFlowItemMoveParametersMetadata],
  };

  export const BeaconWeldItem: FlowItemMetadata = {
    ref: FlowItemRef.WELD,
    codeBlock: 1,
    name: 'welding:weld',
    description: 'welding:weld_desc',
    icon: weldItem,
    displayOrder: 10,
    availableParameters: [...BeaconFlowItemWeldParametersMetadata],
  };

  export const BeaconMoveHomeItem: FlowItemMetadata = {
    ref: FlowItemRef.MOVE_HOME,
    codeBlock: 2,
    name: 'welding:move_home',
    description: 'welding:move_home_desc',
    icon: homeOutline,
    displayOrder: 30,
    availableParameters: [],
  };

  export const BeaconWaitForItem: FlowItemMetadata = {
    ref: FlowItemRef.WAIT_FOR,
    codeBlock: 3,
    name: 'welding:wait_for',
    description: 'welding:wait_for_desc',
    icon: waitForOperator,
    displayOrder: 40,
    availableParameters: BeaconFlowItemWaitForParametersMetadata,
  };

  export const BeaconReportProductionItem: FlowItemMetadata = {
    ref: FlowItemRef.PRODUCTION_REPORTING,
    codeBlock: 4,
    name: 'welding:production_reporting',
    description: 'welding:production_reporting_desc',
    icon: reportProduction,
    displayOrder: 50,
    availableParameters: [ProductionQuanityMetadata],
  };

  export const BeaconTriggerOutputItem: FlowItemMetadata = {
    ref: FlowItemRef.TRIGGER_OUTPUT,
    codeBlock: 5,
    name: 'welding:trigger_output',
    description: 'welding:trigger_output_desc',
    icon: pushOutline,
    displayOrder: 60,
    availableParameters: BeaconFlowItemTriggerOutputParametersMetadata,
    articleId: IntercomArticle.INTEGRATE_WITH_EXTERNAL_EQUIPMENT,
  };

  export const BeaconFlowItemsMetadata = [
    BeaconMoveItem,
    BeaconWeldItem,
    BeaconMoveHomeItem,
    BeaconWaitForItem,
    BeaconReportProductionItem,
    BeaconTriggerOutputItem,
    BeaconSectionItem,
  ];

  type FlowItemByWelderMapping = {
    [key in Welder]: FlowItemMetadata[];
  };

  const CommonItems: FlowItemMetadata[] = [
    BeaconMoveItem,
    BeaconMoveHomeItem,
    BeaconWaitForItem,
    BeaconReportProductionItem,
    BeaconTriggerOutputItem,
    BeaconSectionItem,
  ];

  const SeamTrackingParameters: FlowItemParameterMetadata[] = [
    SeamTrackingEnabledMetadata,
    SeamTrackingLateralEnabledMetadata,
    LateralTrackRightMetadata,
    LateralTrackLeftMetadata,
    LateralSensitivityMetadata,
    SeamTrackingVerticalEnabledMetadata,
    HeightSensitivityMetadata,
  ];

  export const FlowItemsByWelder: FlowItemByWelderMapping = {
    [Welder.ESAB_ARISTO]: [
      ...CommonItems,
      {
        ...BeaconWeldItem,
        availableParameters: [
          ...BeaconWeldItem.availableParameters.filter(
            (p) =>
              ![
                FlowItemParameterRef.ARC_LENGTH,
                FlowItemParameterRef.WIRE_FEED_SPEED,
                FlowItemParameterRef.VOLTAGE,
              ].includes(p.ref as FlowItemParameterRef)
          ),
          ESABTrimMetadata,
          ESABWireFeedSpeedMetadata,
          ESABVoltageMetadata,
        ],
      },
    ],
    [Welder.INVISION]: [...CommonItems, BeaconWeldItem],
    [Welder.MILLER_DELTAWELD]: [
      ...CommonItems,
      {
        ...BeaconWeldItem,
        availableParameters: [
          ...BeaconWeldItem.availableParameters.filter(
            (p) =>
              ![
                FlowItemParameterRef.ARC_LENGTH,
                FlowItemParameterRef.PROCESS,
                FlowItemParameterRef.WIRE_FEED_SPEED,
              ].includes(p.ref as FlowItemParameterRef)
          ),
          MillerDeltaWeldArcLengthMetadata,
          MillerDeltaWeldSequencePreflowTimeMetadata,
          MillerDeltaWeldSequenceProfilePulseModeMetadata,
          MillerDeltaWeldSequencePostflowTimeMetadata,
          MillerDeltaWeldPresetArcControlMetadata,
          MillerDeltaWeldProcessMetadata,
          MillerDeltaWeldWireFeedSpeedMetadata,
          ...SeamTrackingParameters,
        ],
      },
    ],
    [Welder.OTC_WELBEE]: [
      ...CommonItems,
      {
        ...BeaconWeldItem,
        availableParameters: [
          ...BeaconWeldItem.availableParameters.filter(
            (p) =>
              ![
                FlowItemParameterRef.ARC_LENGTH,
                FlowItemParameterRef.PROCESS,
                FlowItemParameterRef.VOLTAGE,
                FlowItemParameterRef.WIRE_FEED_SPEED,
              ].includes(p.ref as FlowItemParameterRef)
          ),
          OTCArcCharacteristic,
          OTCProcessMetadata,
          OTCPenetrationControlMetadata,
          OTCSynergicMode,
          OTCSynergicModeFineAdjustment,
          OTCUseWireFeedSpeed,
          OTCVoltageMetadata,
          OTCWaveFrequency,
          OTCWeldingCurrent,
          OTCWireFeedSpeed,
          ...SeamTrackingParameters,
        ],
      },
    ],
  };

  export enum ApplicationDefaultParameterSetTag {
    GAS = 'gas',
    MATERIAL_TYPE = 'materialType',
    MATERIAL_THICKNESS = 'materialThickness',
    POSITION = 'position',
    PROCESS = 'process',
    WELDER = 'welder',
    WIRE_SIZE = 'wireSize',
    WIRE_TYPE = 'wireType',
    TORCH_TYPE = 'torchType',
  }

  export const ApplicationDefaultParameterSetTagToConfig: Partial<
    Record<ApplicationDefaultParameterSetTag, keyof FlowConfig>
  > = {
    gas: 'gas',
    materialThickness: 'materialThickness',
    materialType: 'materialType',
    wireSize: 'wireDiameter',
    wireType: 'wireType',
  };

  export const InvisionWeldParameterSetTags: ApplicationDefaultParameterSetTagData =
    {
      [ApplicationDefaultParameterSetTag.MATERIAL_THICKNESS]: {
        order: 10,
        required: true,
        label: 'welding:material_thickness',
        options: [
          {
            label: 'welding:material_thickness_00.8',
            searchTerms: ['22'],
            value: MaterialThickness['22GA'],
          },
          {
            label: 'welding:material_thickness_00.9',
            searchTerms: ['20'],
            value: MaterialThickness['20GA'],
          },
          {
            label: 'welding:material_thickness_01.2',
            searchTerms: ['18'],
            value: MaterialThickness['18GA'],
          },
          {
            label: 'welding:material_thickness_01.6',
            searchTerms: ['16'],
            value: MaterialThickness['16GA'],
          },
          {
            label: 'welding:material_thickness_02.0',
            searchTerms: ['14'],
            value: MaterialThickness['14GA'],
          },
          {
            label: 'welding:material_thickness_03.2',
            searchTerms: ['1/8'],
            value: MaterialThickness['1_8'],
          },
          {
            label: 'welding:material_thickness_04.8',
            searchTerms: ['3/16'],
            value: MaterialThickness['3_16'],
          },
          {
            label: 'welding:material_thickness_06.4',
            searchTerms: ['1/4'],
            value: MaterialThickness['1_4'],
          },
          {
            label: 'welding:material_thickness_07.9',
            searchTerms: ['5/16'],
            value: MaterialThickness['5_16'],
          },
          {
            label: 'welding:material_thickness_09.5',
            searchTerms: ['3/8'],
            value: MaterialThickness['3_8'],
          },
          {
            label: 'welding:material_thickness_12.7',
            searchTerms: ['1/2'],
            value: MaterialThickness['1_2'],
          },
        ],
      },
      [ApplicationDefaultParameterSetTag.MATERIAL_TYPE]: {
        order: 25,
        required: true,
        label: 'welding:material_type',
        options: [
          {
            label: 'welding:material_steel',
            value: Material.STEEL,
          },
          {
            label: 'welding:material_stainless',
            value: Material.STAINLESS_STEEL,
          },
          {
            label: 'welding:material_al4x',
            value: Material.ALUMINUM_AL4X,
          },
          {
            label: 'welding:material_al49',
            value: Material.ALUMINUM_AL49X,
          },
          {
            label: 'welding:material_al5x',
            value: Material.ALUMINUM_AL5X,
          },
        ],
      },
      [ApplicationDefaultParameterSetTag.WIRE_TYPE]: {
        order: 29,
        required: true,
        label: 'welding:wire_type',
        options: [
          {
            label: 'welding:wire_type_solid',
            value: WireType.SOLID,
            searchTerms: ['solid'],
          },
          {
            label: 'welding:wire_type_flux_core',
            value: WireType.FLUX,
            searchTerms: ['flux', 'core'],
          },
          {
            label: 'welding:wire_type_metal_core',
            value: WireType.METAL,
            searchTerms: ['metal', 'core'],
          },
        ],
      },
      [ApplicationDefaultParameterSetTag.WIRE_SIZE]: {
        order: 30,
        required: true,
        label: 'welding:wire_diameter',
        options: [
          {
            label: 'welding:wire_size_030',
            value: WireSize['030'],
            searchTerms: ['30', '030'],
          },
          {
            label: 'welding:wire_size_035',
            value: WireSize['035'],
            searchTerms: ['35', '035'],
          },
          {
            label: 'welding:wire_size_040',
            value: WireSize['040'],
            searchTerms: ['40', '040'],
          },
          {
            label: 'welding:wire_size_045',
            value: WireSize['045'],
            searchTerms: ['45', '045'],
          },
          {
            label: 'welding:wire_size_047',
            value: WireSize['047'],
            searchTerms: ['3/64', '047', '47'],
          },

          {
            label: 'welding:wire_size_052',
            value: WireSize['052'],
            searchTerms: ['52', '052'],
          },
          {
            label: 'welding:wire_size_0625',
            value: WireSize['0625'],
            searchTerms: ['1/16', '625'],
          },
        ],
      },
      [ApplicationDefaultParameterSetTag.GAS]: {
        order: 50,
        required: true,
        label: 'welding:gas',
        options: [
          {
            label: 'welding:gas_100co2',
            searchTerms: ['100%', 'CO2'],
            value: Gas.GAS_100CO2,
          },
          {
            label: 'welding:gas_75a_25co2',
            searchTerms: ['75%', 'Argon', '25%', 'CO2'],
            value: Gas.GAS_75A_25CO2,
          },
          {
            label: 'welding:gas_90a_10co2',
            searchTerms: ['90%', 'Argon', '10%', 'CO2'],
            value: Gas.GAS_90A_10CO2,
          },
          {
            label: 'welding:gas_95a_5o',
            searchTerms: ['95%', 'Argon', '5%', 'Oxygen'],
            value: Gas.GAS_95A_5O,
          },
          {
            label: 'welding:gas_98a_2o',
            searchTerms: ['98%', 'Argon', '2%', 'Oxygen'],
            value: Gas.GAS_98A_2O,
          },
          {
            label: 'welding:gas_100a',
            searchTerms: ['100%', 'Argon'],
            value: Gas.GAS_100A,
          },
          {
            label: 'welding:gas_75h_25a',
            searchTerms: ['75%', 'Helium', '25%', 'Argon'],
            value: Gas.GAS_75H_25A,
          },
        ],
      },
      [ApplicationDefaultParameterSetTag.POSITION]: {
        order: 50,
        required: false,
        label: 'welding:weld_position',
        options: [
          {
            label: 'welding:weld_position_flat',
            value: 'flat',
          },
          {
            label: 'welding:weld_position_horizontal',
            value: 'horizontal',
          },
          {
            label: 'welding:weld_position_vertical',
            value: 'vertical',
          },
          {
            label: 'welding:weld_position_overhead',
            value: 'overhead',
          },
        ],
      },
    };

  export const MillerDeltaWeldWeldParameterSetTags: ApplicationDefaultParameterSetTagData =
    {
      [ApplicationDefaultParameterSetTag.GAS]: {
        order: 10,
        required: true,
        label: 'welding:gas',
        options: [
          {
            label: 'welding:gas_100a',
            searchTerms: ['100%', 'Argon'],
            value: Gas.GAS_100A,
            filters: [
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.CV, WeldProcess.PULSE],
                },
              ],
            ],
          },
          {
            label: 'welding:gas_100co2',
            searchTerms: ['100%', 'CO2'],
            value: Gas.GAS_100CO2,
            filters: [
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.CV, WeldProcess.MILLER_RMD],
                },
              ],
            ],
          },
          {
            label: 'welding:gas_50h_50a',
            searchTerms: ['50%', 'Helium', 'Argon'],
            value: Gas.GAS_50H_50A,
            filters: [
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.CV, WeldProcess.PULSE],
                },
              ],
            ],
          },
          {
            label: 'welding:gas_90h_7_5A_2_5CO2',
            searchTerms: ['90%', 'Helim', '7.5%', 'Argon', '2.5%', 'CO2'],
            value: Gas.GAS_90H_7_5A_2_5CO2,
            filters: [
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [
                    WeldProcess.CV,
                    WeldProcess.PULSE,
                    WeldProcess.MILLER_RMD,
                  ],
                },
              ],
            ],
          },
          {
            label: 'welding:gas_66a_33h_1co2',
            searchTerms: ['66%', 'Argon', '33%', 'Helium', '1%', 'CO2'],
            value: Gas.GAS_66A_33H_1CO2,
            filters: [
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.CV, WeldProcess.PULSE],
                },
              ],
            ],
          },
          {
            label: 'welding:gas_75a_25co2',
            searchTerms: ['75%', 'Argon', '25%', 'CO2'],
            value: Gas.GAS_75A_25CO2,
            filters: [
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.CV, WeldProcess.MILLER_RMD],
                },
              ],
            ],
          },
          {
            label: 'welding:gas_80a_20co2',
            searchTerms: ['80%', 'Argon', '20%', 'CO2'],
            value: Gas.GAS_80A_20CO2,
            filters: [
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.CV, WeldProcess.PULSE],
                },
              ],
            ],
          },
          {
            label: 'welding:gas_81a_18h_1co2',
            searchTerms: ['81%', 'Argon', '18%', 'Helium', '1%', 'CO2'],
            value: Gas.GAS_81A_18H_1CO2,
            filters: [
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.CV, WeldProcess.PULSE],
                },
              ],
            ],
          },
          {
            label: 'welding:gas_85a_15co2',
            searchTerms: ['85%', 'Argon', '15%', 'CO2'],
            value: Gas.GAS_85A_15CO2,
            filters: [
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [
                    WeldProcess.CV,
                    WeldProcess.PULSE,
                    WeldProcess.MILLER_HD,
                  ],
                },
              ],
            ],
          },
          {
            label: 'welding:gas_90a_10co2',
            searchTerms: ['90%', 'Argon', '10%', 'CO2'],
            value: Gas.GAS_90A_10CO2,
            filters: [
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [
                    WeldProcess.CV,
                    WeldProcess.PULSE,
                    WeldProcess.MILLER_RMD,
                    WeldProcess.MILLER_HD,
                  ],
                },
              ],
            ],
          },
          {
            label: 'welding:gas_95a_5co2',
            searchTerms: ['95%', 'Argon', '5%', 'CO2'],
            value: Gas.GAS_95A_5CO2,
            filters: [
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.CV, WeldProcess.PULSE],
                },
              ],
            ],
          },
          {
            label: 'welding:gas_98a_2co2',
            searchTerms: ['98%', 'Argon', '2%', 'CO2'],
            value: Gas.GAS_98A_2CO2,
            filters: [
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [
                    WeldProcess.CV,
                    WeldProcess.PULSE,
                    WeldProcess.MILLER_RMD,
                  ],
                },
              ],
            ],
          },
          {
            label: 'welding:gas_95a_5o',
            searchTerms: ['95%', 'Argon', '5%', 'Oxygen'],
            value: Gas.GAS_95A_5O,
            filters: [
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.CV, WeldProcess.PULSE],
                },
              ],
            ],
          },
          {
            label: 'welding:gas_98a_2o',
            searchTerms: ['98%', 'Argon', '2%', 'Oxygen'],
            value: Gas.GAS_98A_2O,
            filters: [
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.CV, WeldProcess.PULSE],
                },
              ],
            ],
          },
        ],
        dependency: ApplicationDefaultParameterSetTag.PROCESS,
      },
      [ApplicationDefaultParameterSetTag.MATERIAL_THICKNESS]: {
        order: 20,
        required: true,
        label: 'welding:material_thickness',
        options: [
          {
            label: 'welding:material_thickness_00.8',
            searchTerms: ['22'],
            value: MaterialThickness['22GA'],
          },
          {
            label: 'welding:material_thickness_00.9',
            searchTerms: ['20'],
            value: MaterialThickness['20GA'],
          },
          {
            label: 'welding:material_thickness_01.2',
            searchTerms: ['18'],
            value: MaterialThickness['18GA'],
          },
          {
            label: 'welding:material_thickness_01.6',
            searchTerms: ['16'],
            value: MaterialThickness['16GA'],
          },
          {
            label: 'welding:material_thickness_02.0',
            searchTerms: ['14'],
            value: MaterialThickness['14GA'],
          },
          {
            label: 'welding:material_thickness_03.2',
            searchTerms: ['1/8'],
            value: MaterialThickness['1_8'],
          },
          {
            label: 'welding:material_thickness_04.8',
            searchTerms: ['3/16'],
            value: MaterialThickness['3_16'],
          },
          {
            label: 'welding:material_thickness_06.4',
            searchTerms: ['1/4'],
            value: MaterialThickness['1_4'],
          },
          {
            label: 'welding:material_thickness_07.9',
            searchTerms: ['5/16'],
            value: MaterialThickness['5_16'],
          },
          {
            label: 'welding:material_thickness_09.5',
            searchTerms: ['3/8'],
            value: MaterialThickness['3_8'],
          },
          {
            label: 'welding:material_thickness_12.7',
            searchTerms: ['1/2'],
            value: MaterialThickness['1_2'],
          },
        ],
      },
      [ApplicationDefaultParameterSetTag.MATERIAL_TYPE]: {
        order: 25,
        required: true,
        label: 'welding:material_type',
        options: [
          {
            label: 'welding:material_steel',
            value: Material.STEEL,
            filters: [
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.CV],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [Gas.GAS_100CO2, Gas.GAS_75A_25CO2],
                },
              ],
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.CV, WeldProcess.PULSE],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [
                    Gas.GAS_80A_20CO2,
                    Gas.GAS_85A_15CO2,
                    Gas.GAS_90A_10CO2,
                    Gas.GAS_95A_5CO2,
                    Gas.GAS_95A_5O,
                  ],
                },
              ],
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.MILLER_RMD],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [
                    Gas.GAS_100CO2,
                    Gas.GAS_75A_25CO2,
                    Gas.GAS_90A_10CO2,
                  ],
                },
              ],
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.MILLER_HD],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [Gas.GAS_85A_15CO2, Gas.GAS_90A_10CO2],
                },
              ],
            ],
          },
          {
            label: 'welding:material_stainless',
            value: Material.STAINLESS_STEEL,
            filters: [
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.CV, WeldProcess.PULSE],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [
                    Gas.GAS_66A_33H_1CO2,
                    Gas.GAS_81A_18H_1CO2,
                    Gas.GAS_90H_7_5A_2_5CO2,
                    Gas.GAS_98A_2CO2,
                    Gas.GAS_98A_2O,
                  ],
                },
              ],
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.MILLER_RMD],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [Gas.GAS_90H_7_5A_2_5CO2, Gas.GAS_98A_2CO2],
                },
              ],
            ],
          },
          {
            label: 'welding:material_al4x',
            value: Material.ALUMINUM_AL4X,
            filters: [
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.CV, WeldProcess.PULSE],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [Gas.GAS_100A, Gas.GAS_50H_50A],
                },
              ],
            ],
          },
          {
            label: 'welding:material_al49',
            value: Material.ALUMINUM_AL49X,
            filters: [
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.CV, WeldProcess.PULSE],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [Gas.GAS_100A, Gas.GAS_50H_50A],
                },
              ],
            ],
          },
          {
            label: 'welding:material_al5x',
            value: Material.ALUMINUM_AL5X,
            filters: [
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.CV, WeldProcess.PULSE],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [Gas.GAS_100A, Gas.GAS_50H_50A],
                },
              ],
            ],
          },
        ],
        dependency: ApplicationDefaultParameterSetTag.GAS,
      },
      [ApplicationDefaultParameterSetTag.WIRE_TYPE]: {
        order: 30,
        required: true,
        label: 'welding:wire_type',
        options: [
          {
            label: 'welding:wire_type_solid',
            value: WireType.SOLID,
            searchTerms: ['solid'],
            filters: [
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.CV],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [
                    Gas.GAS_100CO2,
                    Gas.GAS_75A_25CO2,
                    Gas.GAS_80A_20CO2,
                    Gas.GAS_85A_15CO2,
                    Gas.GAS_90A_10CO2,
                    Gas.GAS_95A_5O,
                  ],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.MATERIAL_TYPE,
                  filter: [Material.STEEL],
                },
              ],
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.PULSE],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [
                    Gas.GAS_80A_20CO2,
                    Gas.GAS_85A_15CO2,
                    Gas.GAS_90A_10CO2,
                    Gas.GAS_95A_5O,
                  ],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.MATERIAL_TYPE,
                  filter: [Material.STEEL],
                },
              ],
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.CV, WeldProcess.PULSE],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [
                    Gas.GAS_66A_33H_1CO2,
                    Gas.GAS_81A_18H_1CO2,
                    Gas.GAS_90H_7_5A_2_5CO2,
                    Gas.GAS_98A_2CO2,
                    Gas.GAS_98A_2O,
                  ],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.MATERIAL_TYPE,
                  filter: [
                    Material.STAINLESS_STEEL,
                    Material.STAINLESS_STEEL_FERRITE,
                  ],
                },
              ],
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.CV, WeldProcess.PULSE],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [Gas.GAS_100A, Gas.GAS_50H_50A],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.MATERIAL_TYPE,
                  filter: [
                    Material.ALUMINUM_AL49X,
                    Material.ALUMINUM_AL4X,
                    Material.ALUMINUM_AL5X,
                  ],
                },
              ],
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.MILLER_RMD],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [
                    Gas.GAS_100CO2,
                    Gas.GAS_75A_25CO2,
                    Gas.GAS_90A_10CO2,
                    Gas.GAS_90H_7_5A_2_5CO2,
                    Gas.GAS_98A_2CO2,
                  ],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.MATERIAL_TYPE,
                  filter: [Material.STEEL, Material.STAINLESS_STEEL],
                },
              ],
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.MILLER_HD],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [Gas.GAS_85A_15CO2, Gas.GAS_90A_10CO2],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.MATERIAL_TYPE,
                  filter: [Material.STEEL],
                },
              ],
            ],
          },
          {
            label: 'welding:wire_type_flux_core',
            value: WireType.FLUX,
            searchTerms: ['flux', 'core'],
            filters: [
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.CV],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [Gas.GAS_100CO2, Gas.GAS_75A_25CO2],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.MATERIAL_TYPE,
                  filter: [Material.STEEL],
                },
              ],
            ],
          },
          {
            label: 'welding:wire_type_metal_core',
            value: WireType.METAL,
            searchTerms: ['metal', 'core'],
            filters: [
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.CV],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [
                    Gas.GAS_75A_25CO2,
                    Gas.GAS_90A_10CO2,
                    Gas.GAS_95A_5CO2,
                    Gas.GAS_95A_5O,
                  ],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.MATERIAL_TYPE,
                  filter: [Material.STEEL],
                },
              ],
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.PULSE],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [
                    Gas.GAS_80A_20CO2,
                    Gas.GAS_90A_10CO2,
                    Gas.GAS_95A_5CO2,
                    Gas.GAS_95A_5O,
                  ],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.MATERIAL_TYPE,
                  filter: [Material.STEEL],
                },
              ],
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.MILLER_RMD],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [Gas.GAS_90A_10CO2],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.MATERIAL_TYPE,
                  filter: [Material.STEEL],
                },
              ],
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.MILLER_HD],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [Gas.GAS_90A_10CO2],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.MATERIAL_TYPE,
                  filter: [Material.STEEL],
                },
              ],
            ],
          },
        ],
        dependency: ApplicationDefaultParameterSetTag.MATERIAL_TYPE,
      },
      [ApplicationDefaultParameterSetTag.WIRE_SIZE]: {
        order: 40,
        required: true,
        label: 'welding:wire_diameter',
        dependency: ApplicationDefaultParameterSetTag.WIRE_TYPE,
        options: [
          {
            label: 'welding:wire_size_030',
            value: WireSize['030'],
            searchTerms: ['30', '030'],
            filters: [
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.CV],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [Gas.GAS_75A_25CO2, Gas.GAS_90A_10CO2],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.MATERIAL_TYPE,
                  filter: [Material.STEEL],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.WIRE_TYPE,
                  filter: [WireType.SOLID],
                },
              ],
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.CV],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [Gas.GAS_98A_2CO2],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.MATERIAL_TYPE,
                  filter: [Material.STAINLESS_STEEL],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.WIRE_TYPE,
                  filter: [WireType.SOLID],
                },
              ],
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.MILLER_RMD],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [Gas.GAS_75A_25CO2, Gas.GAS_90A_10CO2],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.MATERIAL_TYPE,
                  filter: [Material.STEEL],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.WIRE_TYPE,
                  filter: [WireType.SOLID],
                },
              ],
            ],
          },
          {
            label: 'welding:wire_size_035',
            value: WireSize['035'],
            searchTerms: ['35', '035'],
            filters: [
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.CV, WeldProcess.PULSE],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [Gas.GAS_100A, Gas.GAS_50H_50A],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.MATERIAL_TYPE,
                  filter: [
                    Material.ALUMINUM_AL4X,
                    Material.ALUMINUM_AL49X,
                    Material.ALUMINUM_AL5X,
                  ],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.WIRE_TYPE,
                  filter: [WireType.SOLID],
                },
              ],
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.CV],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [Gas.GAS_100CO2, Gas.GAS_75A_25CO2],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.MATERIAL_TYPE,
                  filter: [Material.STEEL],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.WIRE_TYPE,
                  filter: [WireType.SOLID],
                },
              ],
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.CV, WeldProcess.PULSE],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [
                    Gas.GAS_80A_20CO2,
                    Gas.GAS_85A_15CO2,
                    Gas.GAS_90A_10CO2,
                    Gas.GAS_95A_5O,
                  ],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.MATERIAL_TYPE,
                  filter: [Material.STEEL],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.WIRE_TYPE,
                  filter: [WireType.SOLID],
                },
              ],
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.CV, WeldProcess.PULSE],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [
                    Gas.GAS_66A_33H_1CO2,
                    Gas.GAS_81A_18H_1CO2,
                    Gas.GAS_90H_7_5A_2_5CO2,
                    Gas.GAS_98A_2CO2,
                    Gas.GAS_98A_2O,
                  ],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.MATERIAL_TYPE,
                  filter: [
                    Material.STAINLESS_STEEL,
                    Material.STAINLESS_STEEL_FERRITE,
                  ],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.WIRE_TYPE,
                  filter: [WireType.SOLID],
                },
              ],
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.MILLER_RMD],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [
                    Gas.GAS_100CO2,
                    Gas.GAS_75A_25CO2,
                    Gas.GAS_90A_10CO2,
                  ],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.MATERIAL_TYPE,
                  filter: [Material.STEEL],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.WIRE_TYPE,
                  filter: [WireType.SOLID],
                },
              ],
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.MILLER_RMD],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [Gas.GAS_90H_7_5A_2_5CO2, Gas.GAS_98A_2CO2],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.MATERIAL_TYPE,
                  filter: [Material.STAINLESS_STEEL],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.WIRE_TYPE,
                  filter: [WireType.SOLID],
                },
              ],
            ],
          },
          {
            label: 'welding:wire_size_045',
            value: WireSize['045'],
            searchTerms: ['45', '045'],
            filters: [
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.CV],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [Gas.GAS_100CO2],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.MATERIAL_TYPE,
                  filter: [Material.STEEL],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.WIRE_TYPE,
                  filter: [WireType.SOLID, WireType.FLUX],
                },
              ],
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.CV],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [Gas.GAS_75A_25CO2],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.MATERIAL_TYPE,
                  filter: [Material.STEEL],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.WIRE_TYPE,
                  filter: [WireType.SOLID],
                },
              ],
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.CV],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [Gas.GAS_75A_25CO2],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.MATERIAL_TYPE,
                  filter: [Material.STEEL],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.WIRE_TYPE,
                  filter: [WireType.SOLID, WireType.FLUX, WireType.METAL],
                },
              ],
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.CV, WeldProcess.PULSE],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [
                    Gas.GAS_66A_33H_1CO2,
                    Gas.GAS_81A_18H_1CO2,
                    Gas.GAS_90H_7_5A_2_5CO2,
                    Gas.GAS_98A_2CO2,
                    Gas.GAS_98A_2O,
                  ],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.MATERIAL_TYPE,
                  filter: [
                    Material.STAINLESS_STEEL,
                    Material.STAINLESS_STEEL_FERRITE,
                  ],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.WIRE_TYPE,
                  filter: [WireType.SOLID],
                },
              ],
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.CV, WeldProcess.PULSE],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [
                    Gas.GAS_80A_20CO2,
                    Gas.GAS_90A_10CO2,
                    Gas.GAS_95A_5CO2,
                    Gas.GAS_95A_5O,
                  ],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.MATERIAL_TYPE,
                  filter: [Material.STEEL],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.WIRE_TYPE,
                  filter: [WireType.SOLID, WireType.METAL],
                },
              ],
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.CV, WeldProcess.PULSE],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [Gas.GAS_85A_15CO2, Gas.GAS_90A_10CO2],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.MATERIAL_TYPE,
                  filter: [Material.STEEL],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.WIRE_TYPE,
                  filter: [WireType.SOLID],
                },
              ],
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.MILLER_RMD],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [
                    Gas.GAS_100CO2,
                    Gas.GAS_75A_25CO2,
                    Gas.GAS_90A_10CO2,
                  ],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.MATERIAL_TYPE,
                  filter: [Material.STEEL],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.WIRE_TYPE,
                  filter: [WireType.SOLID, WireType.METAL],
                },
              ],
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.MILLER_RMD],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [Gas.GAS_90H_7_5A_2_5CO2, Gas.GAS_98A_2CO2],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.MATERIAL_TYPE,
                  filter: [Material.STAINLESS_STEEL],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.WIRE_TYPE,
                  filter: [WireType.SOLID],
                },
              ],
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.MILLER_HD],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [Gas.GAS_85A_15CO2, Gas.GAS_90A_10CO2],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.MATERIAL_TYPE,
                  filter: [Material.STEEL],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.WIRE_TYPE,
                  filter: [WireType.SOLID, WireType.METAL],
                },
              ],
            ],
          },
          {
            label: 'welding:wire_size_047',
            value: WireSize['047'],
            searchTerms: ['47', '047', '3/64'],
            filters: [
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.CV, WeldProcess.PULSE],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [Gas.GAS_100A, Gas.GAS_50H_50A],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.MATERIAL_TYPE,
                  filter: [
                    Material.ALUMINUM_AL4X,
                    Material.ALUMINUM_AL49X,
                    Material.ALUMINUM_AL5X,
                  ],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.WIRE_TYPE,
                  filter: [WireType.SOLID],
                },
              ],
            ],
          },
          {
            label: 'welding:wire_size_052',
            value: WireSize['052'],
            searchTerms: ['52', '052'],
            filters: [
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.CV],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [Gas.GAS_100CO2],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.MATERIAL_TYPE,
                  filter: [Material.STEEL],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.WIRE_TYPE,
                  filter: [WireType.SOLID, WireType.FLUX],
                },
              ],
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.CV],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [Gas.GAS_75A_25CO2],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.MATERIAL_TYPE,
                  filter: [Material.STEEL],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.WIRE_TYPE,
                  filter: [WireType.SOLID, WireType.FLUX, WireType.METAL],
                },
              ],
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.CV, WeldProcess.PULSE],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [
                    Gas.GAS_80A_20CO2,
                    Gas.GAS_90A_10CO2,
                    Gas.GAS_95A_5CO2,
                    Gas.GAS_95A_5O,
                  ],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.MATERIAL_TYPE,
                  filter: [Material.STEEL],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.WIRE_TYPE,
                  filter: [WireType.SOLID, WireType.METAL],
                },
              ],
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.CV, WeldProcess.PULSE],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [Gas.GAS_85A_15CO2, Gas.GAS_90A_10CO2],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.MATERIAL_TYPE,
                  filter: [Material.STEEL],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.WIRE_TYPE,
                  filter: [WireType.SOLID],
                },
              ],
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.MILLER_RMD],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [Gas.GAS_90A_10CO2],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.MATERIAL_TYPE,
                  filter: [Material.STEEL],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.WIRE_TYPE,
                  filter: [WireType.SOLID],
                },
              ],
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.MILLER_HD],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [Gas.GAS_85A_15CO2, Gas.GAS_90A_10CO2],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.MATERIAL_TYPE,
                  filter: [Material.STEEL],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.WIRE_TYPE,
                  filter: [WireType.SOLID, WireType.METAL],
                },
              ],
            ],
          },
        ],
      },
    };

  export const ESABAristoWeldParameterSetTags: ApplicationDefaultParameterSetTagData =
    {
      [ApplicationDefaultParameterSetTag.MATERIAL_TYPE]: {
        order: 10,
        required: true,
        label: 'welding:material_type',
        options: [
          {
            label: 'welding:material_steel',
            value: Material.STEEL,
          },
          {
            label: 'welding:material_stainless',
            value: Material.STAINLESS_STEEL,
          },
          {
            label: 'welding:material_al4x',
            value: Material.ALUMINUM_AL4X,
          },
          {
            label: 'welding:material_al5x',
            value: Material.ALUMINUM_AL5X,
          },
        ],
      },
      [ApplicationDefaultParameterSetTag.WIRE_SIZE]: {
        order: 30,
        required: true,
        label: 'welding:wire_diameter',
        options: [
          {
            label: 'welding:wire_size_030',
            value: WireSize['030'],
            searchTerms: ['30', '030'],
          },
          {
            label: 'welding:wire_size_035',
            value: WireSize['035'],
            searchTerms: ['35', '035'],
          },
          {
            label: 'welding:wire_size_045',
            value: WireSize['045'],
            searchTerms: ['45', '045'],
          },
          {
            label: 'welding:wire_size_047',
            value: WireSize['047'],
            searchTerms: ['3/64', '047', '47'],
          },
          {
            label: 'welding:wire_size_052',
            value: WireSize['052'],
            searchTerms: ['52', '052'],
          },
          {
            label: 'welding:wire_size_0625',
            value: WireSize['0625'],
            searchTerms: ['1/16', '625'],
          },
        ],
      },
    };

  export const OTCWelbeeWeldParameterSetTags: ApplicationDefaultParameterSetTagData =
    {
      [ApplicationDefaultParameterSetTag.GAS]: {
        order: 10,
        required: true,
        label: 'welding:gas',
        options: [
          {
            label: 'welding:gas_100co2',
            searchTerms: ['100%', 'CO2'],
            value: Gas.GAS_100CO2,
            filters: [
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.CV],
                },
              ],
            ],
          },
          {
            label: 'welding:gas_75a_25co2',
            searchTerms: ['75%', 'Argon', '25%', 'CO2'],
            value: Gas.GAS_75A_25CO2,
            filters: [
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [
                    WeldProcess.CV,
                    WeldProcess.PULSE,
                    WeldProcess.OTC_WAVE_PULSE,
                  ],
                },
              ],
            ],
          },
          {
            label: 'welding:gas_95a_5o',
            searchTerms: ['95%', 'Argon', '5%', 'Oxygen'],
            value: Gas.GAS_95A_5O,
            filters: [
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [
                    WeldProcess.CV,
                    WeldProcess.PULSE,
                    WeldProcess.OTC_WAVE_PULSE,
                  ],
                },
              ],
            ],
          },
          {
            label: 'welding:gas_98a_2o',
            searchTerms: ['98%', 'Argon', '2%', 'Oxygen'],
            value: Gas.GAS_98A_2O,
            filters: [
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [
                    WeldProcess.CV,
                    WeldProcess.PULSE,
                    WeldProcess.OTC_WAVE_PULSE,
                  ],
                },
              ],
            ],
          },
          {
            label: 'welding:gas_100a',
            searchTerms: ['100%', 'Argon'],
            value: Gas.GAS_100A,
            filters: [
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [
                    WeldProcess.CV,
                    WeldProcess.PULSE,
                    WeldProcess.OTC_WAVE_PULSE,
                  ],
                },
              ],
            ],
          },
        ],
        dependency: ApplicationDefaultParameterSetTag.PROCESS,
      },

      [ApplicationDefaultParameterSetTag.MATERIAL_THICKNESS]: {
        order: 20,
        required: true,
        label: 'welding:material_thickness',
        options: [
          {
            label: 'welding:material_thickness_00.8',
            searchTerms: ['22'],
            value: MaterialThickness['22GA'],
          },
          {
            label: 'welding:material_thickness_00.9',
            searchTerms: ['20'],
            value: MaterialThickness['20GA'],
          },
          {
            label: 'welding:material_thickness_01.2',
            searchTerms: ['18'],
            value: MaterialThickness['18GA'],
          },
          {
            label: 'welding:material_thickness_01.6',
            searchTerms: ['16'],
            value: MaterialThickness['16GA'],
          },
          {
            label: 'welding:material_thickness_02.0',
            searchTerms: ['14'],
            value: MaterialThickness['14GA'],
          },
          {
            label: 'welding:material_thickness_03.2',
            searchTerms: ['1/8'],
            value: MaterialThickness['1_8'],
          },
          {
            label: 'welding:material_thickness_04.8',
            searchTerms: ['3/16'],
            value: MaterialThickness['3_16'],
          },
          {
            label: 'welding:material_thickness_06.4',
            searchTerms: ['1/4'],
            value: MaterialThickness['1_4'],
          },
          {
            label: 'welding:material_thickness_07.9',
            searchTerms: ['5/16'],
            value: MaterialThickness['5_16'],
          },
          {
            label: 'welding:material_thickness_09.5',
            searchTerms: ['3/8'],
            value: MaterialThickness['3_8'],
          },
          {
            label: 'welding:material_thickness_12.7',
            searchTerms: ['1/2'],
            value: MaterialThickness['1_2'],
          },
        ],
      },
      [ApplicationDefaultParameterSetTag.MATERIAL_TYPE]: {
        order: 25,
        required: true,
        label: 'welding:material_type',
        options: [
          {
            label: 'welding:material_steel',
            value: Material.STEEL,
            filters: [
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.CV],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [Gas.GAS_100CO2],
                },
              ],
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [
                    WeldProcess.CV,
                    WeldProcess.PULSE,
                    WeldProcess.OTC_WAVE_PULSE,
                  ],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [...CO2_20],
                },
              ],
            ],
          },
          {
            label: 'welding:material_stainless',
            value: Material.STAINLESS_STEEL,
            filters: [
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.CV],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [...CO2_20],
                },
              ],
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [
                    WeldProcess.CV,
                    WeldProcess.PULSE,
                    WeldProcess.OTC_WAVE_PULSE,
                  ],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [...O2_2],
                },
              ],
            ],
          },
          {
            label: 'welding:material_stainless_400_series',
            value: Material.STAINLESS_STEEL_FERRITE,
            filters: [
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [
                    WeldProcess.CV,
                    WeldProcess.PULSE,
                    WeldProcess.OTC_WAVE_PULSE,
                  ],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [...O2_2],
                },
              ],
            ],
          },
          {
            label: 'welding:material_al4x',
            value: Material.ALUMINUM_AL4X,
            filters: [
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [
                    WeldProcess.CV,
                    WeldProcess.PULSE,
                    WeldProcess.OTC_WAVE_PULSE,
                  ],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [...AR_100],
                },
              ],
            ],
          },
          {
            label: 'welding:material_al49',
            value: Material.ALUMINUM_AL49X,
            filters: [
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [
                    WeldProcess.CV,
                    WeldProcess.PULSE,
                    WeldProcess.OTC_WAVE_PULSE,
                  ],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [...AR_100],
                },
              ],
            ],
          },
          {
            label: 'welding:material_al5x',
            value: Material.ALUMINUM_AL5X,
            filters: [
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [
                    WeldProcess.CV,
                    WeldProcess.PULSE,
                    WeldProcess.OTC_WAVE_PULSE,
                  ],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [...AR_100],
                },
              ],
            ],
          },
          {
            label: 'welding:material_cu_si',
            value: Material.CU_SI,
            filters: [
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.CV, WeldProcess.PULSE],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [...AR_100],
                },
              ],
            ],
          },
          {
            label: 'welding:material_cu_al',
            value: Material.CU_AL,
            filters: [
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.CV, WeldProcess.PULSE],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [...AR_100],
                },
              ],
            ],
          },
          {
            label: 'welding:material_inconel',
            value: Material.INCONEL,
            filters: [
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.PULSE],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [...AR_100],
                },
              ],
            ],
          },
        ],
        dependency: ApplicationDefaultParameterSetTag.GAS,
      },
      [ApplicationDefaultParameterSetTag.WIRE_TYPE]: {
        order: 30,
        required: true,
        label: 'welding:wire_type',
        options: [
          {
            label: 'welding:wire_type_solid',
            value: WireType.SOLID,
            searchTerms: ['solid'],
            filters: [
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.CV],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [Gas.GAS_100CO2],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.MATERIAL_TYPE,
                  filter: [Material.STEEL],
                },
              ],
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [
                    WeldProcess.CV,
                    WeldProcess.PULSE,
                    WeldProcess.OTC_WAVE_PULSE,
                  ],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [...CO2_20],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.MATERIAL_TYPE,
                  filter: [Material.STEEL],
                },
              ],
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [
                    WeldProcess.CV,
                    WeldProcess.PULSE,
                    WeldProcess.OTC_WAVE_PULSE,
                  ],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [...O2_2],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.MATERIAL_TYPE,
                  filter: [
                    Material.STAINLESS_STEEL,
                    Material.STAINLESS_STEEL_FERRITE,
                  ],
                },
              ],
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.CV, WeldProcess.PULSE],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [...AR_100],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.MATERIAL_TYPE,
                  filter: [
                    Material.ALUMINUM_AL4X,
                    Material.ALUMINUM_AL49X,
                    Material.ALUMINUM_AL5X,
                    Material.CU_AL,
                    Material.CU_SI,
                  ],
                },
              ],
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.PULSE],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [...AR_100],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.MATERIAL_TYPE,
                  filter: [Material.INCONEL],
                },
              ],
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.OTC_WAVE_PULSE],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [...AR_100],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.MATERIAL_TYPE,
                  filter: [
                    Material.ALUMINUM_AL4X,
                    Material.ALUMINUM_AL49X,
                    Material.ALUMINUM_AL5X,
                  ],
                },
              ],
            ],
          },
          {
            label: 'welding:wire_type_flux_core',
            value: WireType.FLUX,
            searchTerms: ['flux', 'core'],
            filters: [
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.CV],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [...CO2_20],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.MATERIAL_TYPE,
                  filter: [Material.STEEL, Material.STAINLESS_STEEL],
                },
              ],
            ],
          },
          {
            label: 'welding:wire_type_metal_core',
            value: WireType.METAL,
            searchTerms: ['metal', 'core'],
            filters: [
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.CV, WeldProcess.PULSE],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [...CO2_20],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.MATERIAL_TYPE,
                  filter: [Material.STEEL],
                },
              ],
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.CV],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [...CO2_20],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.MATERIAL_TYPE,
                  filter: [Material.STAINLESS_STEEL],
                },
              ],
            ],
          },
        ],
        dependency: ApplicationDefaultParameterSetTag.MATERIAL_TYPE,
      },
      [ApplicationDefaultParameterSetTag.WIRE_SIZE]: {
        order: 40,
        required: true,
        label: 'welding:wire_diameter',
        options: [
          {
            label: 'welding:wire_size_030',
            value: WireSize['030'],
            searchTerms: ['30', '030'],
            filters: [
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.CV],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [Gas.GAS_100CO2],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.MATERIAL_TYPE,
                  filter: [Material.STEEL],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.WIRE_TYPE,
                  filter: [WireType.SOLID],
                },
              ],
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [
                    WeldProcess.CV,
                    WeldProcess.PULSE,
                    WeldProcess.OTC_WAVE_PULSE,
                  ],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [...CO2_20],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.MATERIAL_TYPE,
                  filter: [Material.STEEL],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.WIRE_TYPE,
                  filter: [WireType.SOLID],
                },
              ],
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [
                    WeldProcess.CV,
                    WeldProcess.PULSE,
                    WeldProcess.OTC_WAVE_PULSE,
                  ],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [...O2_2],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.MATERIAL_TYPE,
                  filter: [
                    Material.STAINLESS_STEEL,
                    Material.STAINLESS_STEEL_FERRITE,
                  ],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.WIRE_TYPE,
                  filter: [WireType.SOLID],
                },
              ],
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.CV, WeldProcess.PULSE],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [...AR_100],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.MATERIAL_TYPE,
                  filter: [Material.CU_AL, Material.CU_SI],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.WIRE_TYPE,
                  filter: [WireType.SOLID],
                },
              ],
            ],
          },
          {
            label: 'welding:wire_size_035',
            value: WireSize['035'],
            searchTerms: ['35', '035'],
            filters: [
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.CV],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [Gas.GAS_100CO2],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.MATERIAL_TYPE,
                  filter: [Material.STEEL],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.WIRE_TYPE,
                  filter: [WireType.SOLID],
                },
              ],
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [
                    WeldProcess.CV,
                    WeldProcess.PULSE,
                    WeldProcess.OTC_WAVE_PULSE,
                  ],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [...CO2_20],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.MATERIAL_TYPE,
                  filter: [Material.STEEL],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.WIRE_TYPE,
                  filter: [WireType.SOLID],
                },
              ],
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [
                    WeldProcess.CV,
                    WeldProcess.PULSE,
                    WeldProcess.OTC_WAVE_PULSE,
                  ],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [...O2_2],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.MATERIAL_TYPE,
                  filter: [
                    Material.STAINLESS_STEEL,
                    Material.STAINLESS_STEEL_FERRITE,
                  ],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.WIRE_TYPE,
                  filter: [WireType.SOLID],
                },
              ],
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.CV, WeldProcess.PULSE],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [...AR_100],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.MATERIAL_TYPE,
                  filter: [Material.CU_AL, Material.CU_SI],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.WIRE_TYPE,
                  filter: [WireType.SOLID],
                },
              ],
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.PULSE],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [...AR_100],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.MATERIAL_TYPE,
                  filter: [Material.INCONEL],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.WIRE_TYPE,
                  filter: [WireType.SOLID],
                },
              ],
            ],
          },
          {
            label: 'welding:wire_size_040',
            value: WireSize['040'],
            searchTerms: ['40', '040'],
            filters: [
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.CV],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [Gas.GAS_100CO2],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.MATERIAL_TYPE,
                  filter: [Material.STEEL],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.WIRE_TYPE,
                  filter: [WireType.SOLID],
                },
              ],
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [
                    WeldProcess.CV,
                    WeldProcess.PULSE,
                    WeldProcess.OTC_WAVE_PULSE,
                  ],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [...CO2_20],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.MATERIAL_TYPE,
                  filter: [Material.STEEL],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.WIRE_TYPE,
                  filter: [WireType.SOLID],
                },
              ],
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.CV],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [...O2_2],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.MATERIAL_TYPE,
                  filter: [
                    Material.STAINLESS_STEEL,
                    Material.STAINLESS_STEEL_FERRITE,
                  ],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.WIRE_TYPE,
                  filter: [WireType.SOLID],
                },
              ],
            ],
          },
          {
            label: 'welding:wire_size_045',
            value: WireSize['045'],
            searchTerms: ['45', '045'],
            filters: [
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.CV],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [Gas.GAS_100CO2],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.MATERIAL_TYPE,
                  filter: [Material.STEEL],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.WIRE_TYPE,
                  filter: [WireType.SOLID],
                },
              ],
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.CV, WeldProcess.PULSE],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [...CO2_20],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.MATERIAL_TYPE,
                  filter: [Material.STEEL],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.WIRE_TYPE,
                  filter: [WireType.SOLID, WireType.METAL],
                },
              ],
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.CV],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [...CO2_20],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.MATERIAL_TYPE,
                  filter: [Material.STEEL, Material.STAINLESS_STEEL],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.WIRE_TYPE,
                  filter: [WireType.FLUX, WireType.METAL],
                },
              ],
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [
                    WeldProcess.CV,
                    WeldProcess.PULSE,
                    WeldProcess.OTC_WAVE_PULSE,
                  ],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [...O2_2],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.MATERIAL_TYPE,
                  filter: [
                    Material.STAINLESS_STEEL,
                    Material.STAINLESS_STEEL_FERRITE,
                  ],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.WIRE_TYPE,
                  filter: [WireType.SOLID],
                },
              ],
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.CV, WeldProcess.PULSE],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [...AR_100],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.MATERIAL_TYPE,
                  filter: [
                    Material.ALUMINUM_AL4X,
                    Material.ALUMINUM_AL49X,
                    Material.ALUMINUM_AL5X,
                    Material.CU_AL,
                    Material.CU_SI,
                  ],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.WIRE_TYPE,
                  filter: [WireType.SOLID],
                },
              ],
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.OTC_WAVE_PULSE],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [...AR_100],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.MATERIAL_TYPE,
                  filter: [
                    Material.ALUMINUM_AL4X,
                    Material.ALUMINUM_AL49X,
                    Material.ALUMINUM_AL5X,
                  ],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.WIRE_TYPE,
                  filter: [WireType.SOLID],
                },
              ],
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.PULSE],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [...AR_100],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.MATERIAL_TYPE,
                  filter: [Material.INCONEL],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.WIRE_TYPE,
                  filter: [WireType.SOLID],
                },
              ],
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.OTC_WAVE_PULSE],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [...CO2_20],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.MATERIAL_TYPE,
                  filter: [Material.STEEL],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.WIRE_TYPE,
                  filter: [WireType.SOLID],
                },
              ],
            ],
          },
          {
            label: 'welding:wire_size_052',
            value: WireSize['052'],
            searchTerms: ['52', '052'],
            filters: [
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [WeldProcess.PULSE, WeldProcess.OTC_WAVE_PULSE],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [...CO2_20],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.MATERIAL_TYPE,
                  filter: [Material.STEEL],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.WIRE_TYPE,
                  filter: [WireType.SOLID],
                },
              ],
            ],
          },
          {
            label: 'welding:wire_size_0625',
            value: WireSize['0625'],
            searchTerms: ['1/16', '625'],
            filters: [
              [
                {
                  tag: ApplicationDefaultParameterSetTag.PROCESS,
                  filter: [
                    WeldProcess.CV,
                    WeldProcess.PULSE,
                    WeldProcess.OTC_WAVE_PULSE,
                  ],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.GAS,
                  filter: [...AR_100],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.MATERIAL_TYPE,
                  filter: [
                    Material.ALUMINUM_AL4X,
                    Material.ALUMINUM_AL49X,
                    Material.ALUMINUM_AL5X,
                  ],
                },
                {
                  tag: ApplicationDefaultParameterSetTag.WIRE_TYPE,
                  filter: [WireType.SOLID],
                },
              ],
            ],
          },
        ],
        dependency: ApplicationDefaultParameterSetTag.WIRE_TYPE,
      },
    };

  export const WelderApplicationDefaultParameterSetTagEquipmentMapping: Record<
    string,
    ApplicationDefaultParameterSetTagData
  > = {
    [Welder.INVISION]: InvisionWeldParameterSetTags,
    [Welder.OTC_WELBEE]: OTCWelbeeWeldParameterSetTags,
    [Welder.ESAB_ARISTO]: {
      ...InvisionWeldParameterSetTags,
      ...ESABAristoWeldParameterSetTags,
    },
    [Welder.MILLER_DELTAWELD]: MillerDeltaWeldWeldParameterSetTags,
  };

  export const RadialWeldContiguousRule: FlowRule = {
    execute: (flow: Flow): RuleValidationResponse | undefined => {
      return flowRuleCheckContiguousByType({
        flow,
        itemTypeRef: FlowItemRef.WELD,
        parameterTypeRef: FlowItemParameterRef.WELD_TYPE,
        minRequired: 3,
        primaryTypes: [WeldTypeEnum.RADIAL],
        followedByTypes: [
          WeldTypeEnum.LINEAR,
          WeldTypeEnum.LINEAR_STITCH,
          WeldTypeEnum.RADIAL,
        ],
        followedByStartOffset: 2, // we need to contiguous radials and then the third point can be followed by valid weld type (non-tack)
        suffixMessage: getLocalizedText(
          'welding:more_radial_points_require'
        ) as string,
        sectionBreaksSequence,
      });
    },
  };

  export const LinearWeldContiguousRule: FlowRule = {
    execute: (flow: Flow): RuleValidationResponse | undefined => {
      return flowRuleCheckContiguousByType({
        flow,
        itemTypeRef: FlowItemRef.WELD,
        parameterTypeRef: FlowItemParameterRef.WELD_TYPE,
        minRequired: 2,
        primaryTypes: [WeldTypeEnum.LINEAR, WeldTypeEnum.LINEAR_STITCH],
        suffixMessage: getLocalizedText(
          'welding:more_linear_point_required'
        ) as string,
        precededByTypes: [WeldTypeEnum.RADIAL],
        followedByTypes: [
          WeldTypeEnum.LINEAR,
          WeldTypeEnum.LINEAR_STITCH,
          WeldTypeEnum.RADIAL,
        ],
        sectionBreaksSequence,
      });
    },
  };

  export const FlowValidationRules = [
    RadialWeldContiguousRule,
    LinearWeldContiguousRule,
  ];

  export enum FlowSectionType {
    MULTIPASS = 'multipass',
    STITCH = 'stitch',
  }

  export const ParentSectionTypes = [
    FlowSectionType.MULTIPASS,
    FlowSectionType.STITCH,
  ];

  export interface FlowItemPathSectionAttributes
    extends FlowItemSectionAttributes<FlowSectionType> {}

  export type FlowItemMultipassSectionParentAttributes =
    FlowItemSectionParentAttributes<FlowSectionType> & {
      type: FlowSectionType.MULTIPASS;
      multipassSettings: Required<MultipassSettingsFormData>;
    };

  export type FlowItemMultipassSectionParent =
    FlowItemSectionParent<FlowSectionType> & {
      attributes: FlowItemMultipassSectionParentAttributes;
    };

  export type FlowItemMultipassSection = FlowItem<
    string,
    FlowItemPathSectionAttributes
  >;

  export type FlowItemStitchSectionParentAttributes =
    FlowItemSectionParentAttributes<FlowSectionType> & {
      type: FlowSectionType.STITCH;
      stitchSettings: Required<StitchSettingsFormData>;
    };

  export type FlowItemStitchSectionParent =
    FlowItemSectionParent<FlowSectionType> & {
      attributes: FlowItemStitchSectionParentAttributes;
    };

  export type FlowItemStitchSection = FlowItem<
    string,
    FlowItemPathSectionAttributes
  >;

  export interface StitchSettingsFormData {
    stitchLength?: number;
    stitchLengthLocked: boolean;
    pitch?: number;
    pitchLocked: boolean;
  }

  export interface MultipassPointOffsetFormData {
    startOffsetX: number;
    endOffsetX: number;
    perpendicularToPathY: number;
    stickoutZ: number;
    travelAngle: number;
    workAngle: number;
    reversePath?: boolean;
    weldLibrary?: ScratchpadBucket;
    postActions?: FlowItem[];
  }

  export interface WeldSequenceMarkers {
    firstItem: string;
    lastItem: string;
  }

  export interface MultipassScheduleReference {
    id: string;
    version: number;
  }

  export interface MultipassSettingsFormData {
    reference?: MultipassScheduleReference;
    numberOfPasses?: number;
    workpiecePosition?: WorkPiecePosition;
    angleMode?: AngleModificationMode;
    schedule?: MultipassPointOffsetFormData[];
    startWeldSequence: WeldSequenceMarkers;
    endWeldSequence: WeldSequenceMarkers;
    rootPassPostActions?: FlowItem[];
  }

  export enum FlowItemQuickAction {
    CONVERT_TO_STITCH = 'CONVERT_TO_STITCH',
    CONVERT_TO_MULTIPASS = 'CONVERT_TO_MULTIPASS',
  }
}

function getSeamTrackingEnabledFromForm(
  form: ParametersForm,
  defaultValue = true
): boolean {
  return (
    getParameterValueFromForm<boolean>(
      form,
      BeaconWeldingData.FlowItemParameterRef.SEAM_TRACKING_ENABLED
    ) ?? defaultValue
  );
}

function getLateralEnabledFromForm(
  form: ParametersForm,
  defaultValue = true
): boolean {
  return (
    getParameterValueFromForm<boolean>(
      form,
      BeaconWeldingData.FlowItemParameterRef.SEAM_TRACKING_LATERAL_ENABLED
    ) ?? defaultValue
  );
}
